// IE Hub - Budget Breakdown Page
// Exact implementation based on Figma design

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Collapse,
  Divider,
  TextField,
  Skeleton,
  Alert,
  CircularProgress,
  ArrowForwardIcon,
  EditIcon,
  ExpandMoreIcon,
  KeyboardArrowRightIcon as ArrowRightIcon,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@/ui";
import { Navigate, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { resolveCustomerId } from "@/shared/lib/customerIdResolver";
import { calculateAge } from "@/shared/lib/utils/others";
import {
  normalizeBudgetFrequency,
  toMonthlyBudgetAmount,
} from "@/shared/lib/budgetFrequency";
import { useAlert } from "@/app/providers/AlertProvider";
import {
  BudgetService,
  DebtsService,
  DebtCompaniesService,
  ProfileService,
} from "@/shared/api/services";
import { httpGet, httpPut } from "@/shared/api/http";
import { customerEndpoints, onboardingEndpoints } from "@/shared/api/endpoints";
import {
  mapIncomeDataToSchema,
  mapExpenseDataToSchema,
  getApiFieldNameFromSchemaField,
  getFieldLabelByApiName,
  BUDGET_FIELD_MAPPINGS,
} from "@/shared/lib/budgetFieldMapping";
import {
  evaluateVisibilityRule,
  type VisibilityNode,
} from "@/engine/runtime/visibility/dsl";
import EditFieldDialog from "@/components/budget/EditFieldDialog";
import ReviewBudgetSummaryDialog from "@/components/budget/ReviewBudgetSummaryDialog";
import { mapAssetDataToSchema } from "@/shared/lib/budgetFieldMapping";
import salarySchema from "@/engine/schema/budget/income/1-salary.json";
import benefitsSchema from "@/engine/schema/budget/income/2-benefits.json";
import pensionSchema from "@/engine/schema/budget/income/3-pension.json";
import otherSourcesSchema from "@/engine/schema/budget/income/4-other-sources.json";
import leisureSchema from "@/engine/schema/budget/flexibleExpense/1-leisure.json";
import housekeepingSchema from "@/engine/schema/budget/flexibleExpense/2-housekeeping.json";
import personalCostsSchema from "@/engine/schema/budget/flexibleExpense/3-personal-costs.json";
import savingsSchemaFlex from "@/engine/schema/budget/flexibleExpense/4-savings.json";
import {
  SummaryCardComponent,
  BudgetItemComponent,
  DebtCardComponent,
  SectionHeader,
} from "@/pages/private/dashboard/BudgetPageCom";
import type {
  SummaryCard,
  BudgetItem,
  DebtItem,
} from "@/pages/private/dashboard/BudgetPageCom";
import type {
  ApiResponse,
  CalculatedTotals,
  IncomeTriggerRecord,
} from "@/shared/api/types";
import { TriggersProvider } from "@/shared/warnings/TriggersContext";
import { CustomerMetadataContext } from "@/shared/warnings/CustomerMetadataContext";
import { WarningsRefreshContext } from "@/shared/warnings/WarningsRefreshContext";
import type { OpenActionSpec } from "@/shared/warnings/types";
import ProfileFormDialog from "@/components/profile/ProfileFormDialog";
import {
  computeConfirmBeforeClearPlan,
  type BudgetSnapshots,
  type ProfileBudgetContext,
} from "@/shared/budget/confirmBeforeClear";
import {
  incomeOnboardingSchemas,
  partnerEmploymentSchema,
  PROFILE_DIALOG_STEP_MAP,
  PROFILE_DIALOG_TITLES,
  PROFILE_DIALOG_SUCCESS_MESSAGES,
  type ProfileDialogKey,
} from "@/pages/private/noNavButFooter/UpdateBudgetPageCom/constants";
import type { CustomerDebtRecord } from "@/shared/api/types";
import type { AccountTypeRecord } from "@/shared/api/services/lenderService";
import { useAuth } from "@/shared/state/store";
import { ThemeColours } from "@/clients/acme/config/colours";
import SmartReviewDialog from "@/components/SmartReviewDialog";
import { normaliseCount } from "@/shared/lib/utils/normaliseCount";
import SimpleLoadingScreen from "@/components/SimpleLoadingScreen";
import { UU_SHOW_HIDE_INFO } from "@/shared/uuShowHide";
import { useNorthernIrelandDetection } from "@/shared/hooks/useNorthernIrelandDetection";
import { UI_MESSAGES } from "../../../constants/ui";

// Types moved to @BudgetPageCom/types

// ---- Helpers for totals/counts -------------------------------------------------
type SectionStats = { total: number; answered: number; monthlyTotal: number };

type IncomeGroupKey = "salary" | "benefits" | "pension" | "otherSources";
type FlexibleGroupKey =
  | "leisure"
  | "housekeeping"
  | "personalCosts"
  | "savings";

// ---- Trigger context helpers (parity with updateBudgetPage) -------------------
type TriggersContextShape = {
  income: {
    exceeded: Record<string, boolean>;
    maxByField: Record<string, number>;
  };
  expense: {
    exceeded: Record<string, boolean>;
    maxByField: Record<string, number>;
  };
  maxByField: Record<string, number>;
};

type ProfileDialogState = {
  key: ProfileDialogKey;
  stepIndex: number;
  schema: unknown;
  title: string;
  initialData: Record<string, unknown>;
};

type AdditionalNotesSectionProps = {
  profileData: any | null;
  setProfileData: React.Dispatch<React.SetStateAction<any | null>>;
  userId?: number;
};

/**
 * Additional Notes autosave is intentionally isolated from the rest of the Budget page.
 * The Budget page is very large, and keeping notes as parent state makes typing feel laggy
 * due to whole-page re-renders on every keystroke.
 *
 * UX: We *do not* show a "Saving..." message while autosaving (per request), but we still
 * show an error if saving fails.
 */
const AdditionalNotesSection = React.memo(function AdditionalNotesSection(
  props: AdditionalNotesSectionProps
) {
  const { profileData, setProfileData, userId } = props;
  const { showAlert } = useAlert();

  const [notes, setNotes] = useState<string>("");
  const [notesError, setNotesError] = useState<string | null>(null);
  const [hasCustomerRecordForNotes, setHasCustomerRecordForNotes] =
    useState<boolean>(false);

  const additionalNotesDirtyRef = useRef<boolean>(false);
  const lastSavedAdditionalNotesRef = useRef<string>("");
  const additionalNotesDebounceTimerRef = useRef<number | undefined>(undefined);
  const customerRecordForNotesRef = useRef<Record<string, any> | null>(null);
  const saveSeqRef = useRef<number>(0);

  const saveAdditionalNotes = useCallback(
    async (nextValue: string) => {
      const baseRecord =
        (profileData as Record<string, any> | null) ??
        customerRecordForNotesRef.current;
      if (!baseRecord) {
        setNotesError("Couldn’t save notes right now.");
        return;
      }

      const nextNotes =
        typeof nextValue === "string" ? nextValue.slice(0, 255) : "";
      if (nextNotes === lastSavedAdditionalNotesRef.current) {
        additionalNotesDirtyRef.current = false;
        return;
      }

      setNotesError(null);

      const saveSeq = ++saveSeqRef.current;

      try {
        const payload: Record<string, any> = {
          ...baseRecord,
          textField1: nextNotes,
        };

        // Guard: backend sometimes treats { customerId: userId } as invalid; match onboarding behaviour
        if (
          typeof payload.customerId === "number" &&
          typeof userId === "number" &&
          payload.customerId === userId
        ) {
          delete payload.customerId;
        }

        const response = await httpPut<Record<string, any>>(
          onboardingEndpoints.updateCustomer,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // If a newer save started after this one, ignore the results to avoid out-of-order clobbers.
        if (saveSeq !== saveSeqRef.current) return;

        const updatedRecord = response?.data;
        if (updatedRecord && typeof updatedRecord === "object") {
          setProfileData(updatedRecord);
          customerRecordForNotesRef.current = updatedRecord;
          try {
            localStorage.setItem("customerData", JSON.stringify(updatedRecord));
          } catch {
            // ignore storage errors
          }
        } else {
          setProfileData((prev: Record<string, any> | null) =>
            prev ? { ...(prev as any), textField1: nextNotes } : prev
          );
          try {
            const raw = localStorage.getItem("customerData");
            if (raw) {
              const parsed = JSON.parse(raw) as Record<string, any>;
              localStorage.setItem(
                "customerData",
                JSON.stringify({ ...parsed, textField1: nextNotes })
              );
            }
          } catch {
            // ignore storage errors
          }
        }

        lastSavedAdditionalNotesRef.current = nextNotes;
        additionalNotesDirtyRef.current = false;
      } catch {
        setNotesError("Couldn’t save notes. Please try again.");
      }
    },
    [profileData, setProfileData, userId]
  );

  // Keep additional notes in sync with loaded customer profile, without clobbering in-progress edits
  useEffect(() => {
    let baseRecord: Record<string, any> | null =
      profileData && typeof profileData === "object"
        ? (profileData as Record<string, any>)
        : null;

    if (!baseRecord && typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem("customerData");
        if (raw) {
          const parsed = JSON.parse(raw) as Record<string, any>;
          if (parsed && typeof parsed === "object") {
            baseRecord = parsed;
          }
        }
      } catch {
        // ignore storage errors
      }
    }

    customerRecordForNotesRef.current = baseRecord;
    setHasCustomerRecordForNotes(!!baseRecord);

    const incoming =
      baseRecord && typeof baseRecord.textField1 === "string"
        ? String(baseRecord.textField1)
        : "";

    if (!additionalNotesDirtyRef.current) {
      setNotes(incoming);
      lastSavedAdditionalNotesRef.current = incoming;
      setNotesError(null);
    }
  }, [profileData]);

  // Debounced autosave for additional notes (legacy parity: save after user stops typing)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasCustomerRecordForNotes) return;
    if (!additionalNotesDirtyRef.current) return;

    if (additionalNotesDebounceTimerRef.current !== undefined) {
      window.clearTimeout(additionalNotesDebounceTimerRef.current);
      additionalNotesDebounceTimerRef.current = undefined;
    }

    additionalNotesDebounceTimerRef.current = window.setTimeout(() => {
      void saveAdditionalNotes(notes);
      additionalNotesDebounceTimerRef.current = undefined;
    }, 800);

    return () => {
      if (additionalNotesDebounceTimerRef.current !== undefined) {
        window.clearTimeout(additionalNotesDebounceTimerRef.current);
        additionalNotesDebounceTimerRef.current = undefined;
      }
    };
  }, [notes, hasCustomerRecordForNotes, saveAdditionalNotes]);

  return (
    <Box sx={{ mt: 6, mb: 5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#1E293B",
          }}
        >
          Additional Notes (Optional)
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <TextField
        multiline
        minRows={4}
        value={notes}
        onChange={(
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          additionalNotesDirtyRef.current = true;
          setNotesError(null);
          setNotes(String(e.target.value ?? "").slice(0, 255));
        }}
        onKeyDown={(e) => {
          if (
            notes.length >= 255 &&
            e.key.length === 1 &&
            !e.ctrlKey &&
            !e.metaKey
          ) {
            showAlert({
              severity: "warning",
              message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
            });
          }
        }}
        onPaste={(e) => {
          const pastedText = e.clipboardData.getData("text");
          const selectionStart = (e.target as any).selectionStart || 0;
          const selectionEnd = (e.target as any).selectionEnd || 0;
          const currentLength = notes.length;
          const maxLength = 255;
          if (
            currentLength - (selectionEnd - selectionStart) + pastedText.length >
            maxLength
          ) {
            showAlert({
              severity: "warning",
              message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
            });
          }
        }}
        onBlur={() => {
          if (typeof window !== "undefined") {
            if (additionalNotesDebounceTimerRef.current !== undefined) {
              window.clearTimeout(additionalNotesDebounceTimerRef.current);
              additionalNotesDebounceTimerRef.current = undefined;
            }
          }

          if (hasCustomerRecordForNotes && additionalNotesDirtyRef.current) {
            void saveAdditionalNotes(notes);
          }
        }}
        disabled={!hasCustomerRecordForNotes}
        placeholder="If there is any additional information relating to your Personal and Household details, income, fixed and flexible expenses, or debts please add it in this section."
        validation={{ maxLength: 255 }}
        showCharacterCount
        helperBottom={notesError ?? undefined}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "auto",
            alignItems: "flex-start",
          },
        }}
        inputProps={{
          maxLength: 255,
        }}
      />
    </Box>
  );
});

const toUpperSnake = (apiFieldName: string): string =>
  apiFieldName
    .replace(/([A-Z])/g, "_$1")
    .toUpperCase()
    .replace(/^_/, "");

const upperSnakeToCamel = (s: string): string =>
  s
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part, idx) =>
      idx === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");

const normalizeTriggerFieldUpper = (raw: unknown): string => {
  const s =
    typeof raw === "string"
      ? raw
      : raw === null || raw === undefined
        ? ""
        : String(raw);
  const trimmed = s.trim();
  if (!trimmed) return "";

  // Normalize "House Rent", "house-rent", "houseRent", "HOUSE_RENT" -> "HOUSE_RENT"
  let out = trimmed
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

  // Some trigger keys are enumerated (e.g. OTHER_SALARY_WAGES_1). If a number suffix
  // is present without an underscore, insert one: OTHER_SALARY_WAGES1 -> OTHER_SALARY_WAGES_1
  out = out.replace(/([A-Z])(\d+)$/g, "$1_$2");
  return out;
};

const normalizeToMonthly = (
  amount: number | undefined,
  freqRaw: unknown
): number | undefined => {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return amount;
  const freq =
    normalizeBudgetFrequency(
      typeof freqRaw === "string" ? freqRaw : String(freqRaw ?? "")
    ) ?? "monthly";
  return toMonthlyBudgetAmount(amount, freq);
};

const buildApiAmountToFrequencyFieldMap = (): Record<string, string> => {
  const out: Record<string, string> = {};
  (Object.values(BUDGET_FIELD_MAPPINGS) as any[]).forEach((cat) => {
    if (!cat || typeof cat !== "object") return;
    Object.values(cat as any).forEach((def: any) => {
      if (!def || typeof def !== "object") return;
      if (typeof def.amount === "string" && typeof def.frequency === "string") {
        out[def.amount] = def.frequency;
      }
    });
  });
  return out;
};

const API_AMOUNT_TO_FREQUENCY_FIELD = buildApiAmountToFrequencyFieldMap();
const buildAllApiAmountFieldsSet = (): Set<string> => {
  const out = new Set<string>();
  (Object.values(BUDGET_FIELD_MAPPINGS) as any[]).forEach((cat) => {
    if (!cat || typeof cat !== "object") return;
    Object.values(cat as any).forEach((def: any) => {
      if (!def || typeof def !== "object") return;
      if (typeof def.amount === "string") out.add(def.amount);
    });
  });
  return out;
};

const ALL_API_AMOUNT_FIELDS = buildAllApiAmountFieldsSet();

const buildTriggerKeyToApiFieldMap = (): Record<string, string> => {
  const out: Record<string, string> = {};
  Array.from(ALL_API_AMOUNT_FIELDS).forEach((apiField) => {
    const key = normalizeTriggerFieldUpper(toUpperSnake(apiField));
    if (!key) return;
    out[key] = apiField;
    out[key.replace(/_/g, "")] = apiField;
  });
  return out;
};

const TRIGGER_KEY_TO_API_FIELD = buildTriggerKeyToApiFieldMap();

const parseCurrencyAmount = (val: string): number | null => {
  if (!val || val === "-") return null;
  const num = Number(String(val).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(num) ? num : null;
};

const toMonthlyAmount = (amount: number, frequency: string): number => {
  const freq = normalizeBudgetFrequency(frequency) ?? "monthly";
  return toMonthlyBudgetAmount(amount, freq);
};

const normalizeDebtFrequency = (frequency: unknown): string => {
  const code = String(frequency ?? "")
    .trim()
    .toUpperCase();
  if (code === "W") return "week";
  if (code === "4W" || code === "FW" || code === "F") return "4weekly";
  if (code === "Y" || code === "A") return "year";
  return "month";
};

const formatDebtCurrency = (value: unknown): string => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "-";
  return `£${num.toFixed(2)}`;
};

const PRIORITY_ARREARS_CODES: ReadonlySet<string> = new Set([
  "GA",
  "EE",
  "WS",
  "WA",
  "HR",
  "MG",
  "SE",
  "CT",
]);

const splitDebtsByPriority = (
  records: CustomerDebtRecord[]
): { priority: CustomerDebtRecord[]; nonPriority: CustomerDebtRecord[] } => {
  return records.reduce(
    (acc, raw) => {
      const debtTypeStr = String(raw.debtType || "").toLowerCase();
      const sourceStr = String(raw.source ?? "")
        .trim()
        .toLowerCase();
      const accTypeCode =
        typeof raw.accTypeCode === "string" ? raw.accTypeCode : "";
      const isArrearsCode =
        accTypeCode &&
        PRIORITY_ARREARS_CODES.has(accTypeCode.trim().toUpperCase());
      const isPriority =
        (debtTypeStr.includes("priority") && !debtTypeStr.includes("non")) ||
        sourceStr === "customer_expenses" ||
        isArrearsCode;
      if (isPriority) {
        acc.priority.push(raw);
      } else {
        acc.nonPriority.push(raw);
      }
      return acc;
    },
    {
      priority: [] as CustomerDebtRecord[],
      nonPriority: [] as CustomerDebtRecord[],
    }
  );
};

const mapDebtRecordsToItems = (records: CustomerDebtRecord[]): DebtItem[] =>
  records.map((raw) => {
    const amountOwedVal =
      typeof raw.amountOwed === "number"
        ? raw.amountOwed
        : typeof (raw as any).balance === "number"
          ? (raw as any).balance
          : typeof (raw as any).mortgageOwingAmt === "number"
            ? (raw as any).mortgageOwingAmt
            : null;
    const repaymentVal =
      typeof raw.repayment === "number"
        ? raw.repayment
        : typeof raw.repaymentMonthly === "number"
          ? raw.repaymentMonthly
          : null;

    const accTypeCode =
      typeof raw.accTypeCode === "string" ? raw.accTypeCode : undefined;
    const debtTypeRaw =
      typeof raw.debtType === "string" ? raw.debtType : undefined;

    return {
      id: String(raw.id ?? ""),
      companyName: raw.creditor ?? "",
      referenceNumber: raw.accountNumber ?? "-",
      debtType: (accTypeCode || debtTypeRaw || "") as string,
      amountOwed:
        amountOwedVal !== null ? formatDebtCurrency(amountOwedVal) : "-",
      repaymentAmount:
        repaymentVal !== null ? formatDebtCurrency(repaymentVal) : "-",
      frequency: normalizeDebtFrequency(raw.frequency),
    };
  });

export const CircleNumber = ({
  number,
  size = 24,
  bgColor = "#0f0",
  color = "#fff",
}: {
  number: number;
  size?: number;
  bgColor?: string;
  color?: string;
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      bgcolor: bgColor,
      color: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 600,
    }}
  >
    <Typography component="span" sx={{ fontSize: Math.floor(size * 0.8) }}>
      {number}
    </Typography>
  </Box>
);

const formatGBP = (n: number): string =>
  `£${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BudgetPageInner: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [smartReviewOpen, setSmartReviewOpen] = useState(false);

  // Northern Ireland detection for label customizations (Council Rates, water, savings)
  const { isNorthernIreland, loading: niLoading } =
    useNorthernIrelandDetection();

  // Initial-load failure handling:
  // - If the first load cannot fetch essential budget data, do NOT render totals as £0.00.
  // - Show a blocking retry screen (retry once automatically, plus explicit Retry/Refresh actions).
  // - For subsequent refresh failures, keep showing last-known-good data and surface a warning banner.
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<string>("");
  const [refreshLoadWarning, setRefreshLoadWarning] = useState<string>("");
  const lastRefreshWarningAtRef = useRef<number>(0);

  const [triggersCount, setTriggersCount] = useState(-1);
  const [incomeTriggers, setIncomeTriggers] = useState<IncomeTriggerRecord[]>(
    []
  );

  const [loading, setLoading] = useState(true);
  const [isEditFieldDialogOpen, setIsEditFieldDialogOpen] = useState(false);
  const [isReviewBudgetDialogOpen, setIsReviewBudgetDialogOpen] =
    useState(false);
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  const [editDialogConfig, setEditDialogConfig] = useState<{
    groupKey:
      | "salary"
      | "benefits"
      | "pension"
      | "otherSources"
      | "properties"
      | "otherAssets"
      | "household"
      | "utilities"
      | "care"
      | "transport"
      | "school"
      | "pensionInsurance"
      | "professionals"
      | "otherSourcesFixed"
      | "leisure"
      | "housekeeping"
      | "personalCosts"
      | "savings"
      | "debtsAdministration";
    fieldKey: string;
    title: string;
  } | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    income: true,
    assets: false,
    fixedExpense: false,
    flexibleExpense: false,
    debts: false,
  });
  const [activeCardId, setActiveCardId] = useState<string | null>("income");
  const fetchRef = useRef<() => void>(() => {});
  const isFetchingRef = useRef(false);
  // Keep the last successfully resolved customerId for this page so profile-dialog
  // saves (and confirm-before-clear checks) don't depend on localStorage fallbacks.
  const resolvedCustomerIdRef = useRef<number | null>(null);
  const lastFetchTimestampRef = useRef<number>(0);
  const STALE_TIME = 5 * 60 * 1000; // 5 minutes - data is considered fresh for this duration
  const hasLoadedOnceRef = useRef<boolean>(false);

  const sleep = useCallback((ms: number) => {
    if (typeof window === "undefined") return Promise.resolve();
    return new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), ms);
    });
  }, []);

  const shouldShowRefreshWarning = useCallback((): boolean => {
    const now = Date.now();
    if (now - lastRefreshWarningAtRef.current < 30_000) {
      return false;
    }
    lastRefreshWarningAtRef.current = now;
    return true;
  }, []);

  // Refs for scrolling to sections
  const incomeSectionRef = React.useRef<HTMLDivElement>(null);
  const assetsSectionRef = React.useRef<HTMLDivElement>(null);
  const fixedExpenseSectionRef = React.useRef<HTMLDivElement>(null);
  const flexibleExpenseSectionRef = React.useRef<HTMLDivElement>(null);
  const debtsSectionRef = React.useRef<HTMLDivElement>(null);
  const [incomeRecord, setIncomeRecord] = useState<any | null>(null);
  const [incomeNotesRecord, setIncomeNotesRecord] = useState<
    Record<string, any>
  >({});
  const [assetRecord, setAssetRecord] = useState<any | null>(null);
  const [assetNotesRecord, setAssetNotesRecord] = useState<Record<string, any>>(
    {}
  );
  const [expenseRecord, setExpenseRecord] = useState<any | null>(null);
  const [expenseNotesRecord, setExpenseNotesRecord] = useState<
    Record<string, any>
  >({});

  // Keep last non-null snapshots to avoid transient nulls during refresh causing
  // confirm-before-clear to miss existing values.
  const lastIncomeSnapshotRef = useRef<any | null>(null);
  const lastIncomeNotesSnapshotRef = useRef<Record<string, any> | null>(null);
  const lastExpenseSnapshotRef = useRef<any | null>(null);
  const lastExpenseNotesSnapshotRef = useRef<Record<string, any> | null>(null);
  const [profileData, setProfileData] = useState<any | null>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number>(0);
  const [priorityDebts, setPriorityDebts] = useState<DebtItem[]>([]);
  const [nonPriorityDebts, setNonPriorityDebts] = useState<DebtItem[]>([]);
  const [debtsLoading, setDebtsLoading] = useState<boolean>(true);
  const [debtAdminFee, setDebtAdminFee] = useState<{
    amount: string;
    frequency: string;
  } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [calculatedTotals, setCalculatedTotals] =
    useState<CalculatedTotals | null>(null);

  // ---- Warning engine parity with updateBudgetPage (profile token dialogs + refresh) ----
  const [warningsRefreshKey, setWarningsRefreshKey] = useState(0);
  const [profileDialogState, setProfileDialogState] =
    useState<ProfileDialogState | null>(null);
  const [isProfileDialogSaving, setIsProfileDialogSaving] = useState(false);
  const CONFIRM_REMOVE_SECTION_MESSAGE =
    "Updating your profile status will change your recorded budget. You should review your monthly summary before sharing your budget. Do you wish to continue?";
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Prevent "click-through": when the confirm overlay appears, the user's original
  // click/tap on the underlying Save button can land on the newly-rendered Continue button.
  // We arm the buttons after a short delay so only an intentional second click confirms.
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [confirmClearedLabels, setConfirmClearedLabels] = useState<string[]>(
    []
  );
  const [confirmPendingAction, setConfirmPendingAction] = useState<
    (() => Promise<void>) | null
  >(null);
  const isDebugConfirmEnabled = useCallback((): boolean => {
    if (typeof window === "undefined") return false;
    try {
      return (
        new URLSearchParams(window.location.search).get("debugConfirm") === "1"
      );
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (confirmOpen) {
      // eslint-disable-next-line no-console
      console.log("[confirmBeforeClear] confirmOpen=true");
    }
  }, [confirmOpen]);

  useEffect(() => {
    if (!confirmOpen) {
      setConfirmArmed(false);
      return;
    }
    setConfirmArmed(false);
    if (typeof window === "undefined") return;
    const t = window.setTimeout(() => {
      setConfirmArmed(true);
    }, 350);
    return () => {
      window.clearTimeout(t);
    };
  }, [confirmOpen]);

  const scrollTimeoutRef = React.useRef<number | undefined>(undefined);
  const lastDialogOpenTsRef = React.useRef<number>(0);

  const cancelScheduledScroll = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (scrollTimeoutRef.current !== undefined) {
      window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = undefined;
    }
  }, []);

  const findScrollParent = useCallback(
    (element: HTMLElement | null): HTMLElement | null => {
      if (!element || typeof window === "undefined") {
        return null;
      }

      let parent: HTMLElement | null = element.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflow = style.overflow;
        const canScroll =
          /(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflow);

        if (canScroll && parent.scrollHeight > parent.clientHeight) {
          return parent;
        }

        parent = parent.parentElement;
      }

      return null;
    },
    []
  );

  const performScrollToSection = useCallback(
    (sectionId: string) => {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return;
      }

      const element = document.getElementById(`section-${sectionId}`);
      if (!element) {
        return;
      }

      const scrollParent = findScrollParent(element);
      const offset = 120;

      if (scrollParent) {
        const containerRect = scrollParent.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetScrollTop =
          scrollParent.scrollTop + elementRect.top - containerRect.top - offset;

        scrollParent.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: "smooth",
        });
        return;
      }

      const targetTop =
        element.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
    },
    [findScrollParent]
  );

  const scheduleScrollToSection = useCallback(
    (sectionId: string, delay: number) => {
      if (typeof window === "undefined") {
        return;
      }

      cancelScheduledScroll();

      scrollTimeoutRef.current = window.setTimeout(() => {
        performScrollToSection(sectionId);
        scrollTimeoutRef.current = undefined;
      }, delay);
    },
    [cancelScheduledScroll, performScrollToSection]
  );

  useEffect(
    () => () => {
      cancelScheduledScroll();
    },
    [cancelScheduledScroll]
  );

  // Summary cards computed after stats

  // ---- Guard to prevent double-opening the edit dialog -----------------------
  const safeOpenEditDialog = React.useCallback(() => {
    const now = Date.now();
    if (now - lastDialogOpenTsRef.current < 300) {
      return;
    }
    lastDialogOpenTsRef.current = now;
    setIsEditFieldDialogOpen(true);
  }, []);

  // ---- Visibility helpers ----------------------------------------------------
  const readNumber = useCallback((obj: any, ...keys: string[]): number => {
    for (const key of keys) {
      const v = obj && obj[key];
      if (typeof v === "number") return v;
      if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
    }
    return 0;
  }, []);

  const readLocalCustomerData = useCallback((): any | null => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("customerData");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const getChildrenCounts = useCallback(() => {
    const local = readLocalCustomerData();
    const under16 =
      readNumber(
        profileData,
        "dependentChildrenUnder16",
        "dependantsUnder16"
      ) || readNumber(local, "dependentChildrenUnder16", "dependantsUnder16");
    const over16 =
      readNumber(
        profileData,
        "dependentChildrenAged16Plus",
        "dependentChildrenaged16plus",
        "dependants16Plus"
      ) ||
      readNumber(
        local,
        "dependentChildrenAged16Plus",
        "dependentChildrenaged16plus"
      );
    return { under16, over16, total: under16 + over16 };
  }, [profileData, readNumber, readLocalCustomerData]);

  // Removed getHasPartner: budget breakdown now adheres strictly to schema rules per field

  // ------- Shared visibility helpers (JSON-schema-based) ------------------------

  const buildBaseVisibilityContext = useCallback((): Record<
    string,
    unknown
  > => {
    const local = readLocalCustomerData();

    const customerEmploymentStatus =
      (profileData && (profileData as any).customerEmploymentStatus) ??
      (local && (local as any).customerEmploymentStatus) ??
      "";

    const partnerEmploymentStatus =
      (profileData && (profileData as any).partnerEmploymentStatus) ??
      (local && (local as any).partnerEmploymentStatus) ??
      "";

    const jointAccount =
      (profileData && (profileData as any).jointAccount) ??
      (local && (local as any).jointAccount) ??
      "";

    const accommodationType =
      (profileData &&
        ((profileData as any).housingTenure ??
          (profileData as any).accommodationType ??
          (profileData as any).accomodationType)) ??
      (local &&
        ((local as any).housingTenure ?? (local as any).accomodationType)) ??
      "";

    const { under16, over16 } = getChildrenCounts();
    const haveChildrenUnder18 = under16 + over16 > 0 ? "Y" : "N";

    // Age metadata is not strictly required for most income/expense fields;
    // we derive simple ages here when DOB is available.
    const computeAgeFrom = (dobStr: unknown): number => {
      if (!dobStr || typeof dobStr !== "string") return 0;
      const age = calculateAge(dobStr);
      return typeof age === "number" && Number.isFinite(age) ? age : 0;
    };

    const customerDob =
      (profileData &&
        ((profileData as any).debtor1DateOfBirthStr ??
          (profileData as any).dateOfBirth)) ??
      (local &&
        ((local as any).debtor1DateOfBirthStr ?? (local as any).dateOfBirth));
    const partnerDob =
      (profileData &&
        ((profileData as any).debtor2DateOfBirthStr ??
          (profileData as any).partnerDateOfBirth)) ??
      (local &&
        ((local as any).debtor2DateOfBirthStr ??
          (local as any).partnerDateOfBirth));

    const customerAge = computeAgeFrom(customerDob);
    const partnerAge = computeAgeFrom(partnerDob);

    const vehiclesRaw =
      (profileData &&
        ((profileData as any).noOfVehiclesInHouseholds ??
          (profileData as any).vehicles ??
          (profileData as any).vehiclesCount)) ??
      (local &&
        ((local as any).noOfVehiclesInHouseholds ?? (local as any).vehicles));
    const vehicleCount =
      typeof vehiclesRaw === "number" ? vehiclesRaw : Number(vehiclesRaw || 0);
    const haveVehicle = vehicleCount > 0 ? "Y" : "N";

    return {
      customerEmploymentStatus,
      partnerEmploymentStatus,
      jointAccount,
      accommodationType,
      customerAge,
      partnerAge,
      dependentChildrenUnder16: under16,
      dependentChildrenAged16Plus: over16,
      haveChildrenUnder18,
      haveVehicle,
      noOfVehiclesInHouseholds: vehicleCount || undefined,
    };
  }, [profileData, readLocalCustomerData, getChildrenCounts]);

  // Provide the same metadata/triggers contexts used by updateBudgetPage so the warning engine
  // (Quick check prompts) behaves identically in the Budget Page edit dialog.
  const customerMetadataForWarnings = useMemo(() => {
    const base = buildBaseVisibilityContext();
    const out: import("@/shared/warnings/CustomerMetadataContext").CustomerMetadata =
      {};

    const putStr = (
      key:
        | "jointAccount"
        | "customerEmploymentStatus"
        | "partnerEmploymentStatus"
        | "haveChildrenUnder18"
        | "accommodationType",
      v: unknown
    ) => {
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (!s) return;
      out[key] = s;
    };

    const putNum = (
      key:
        | "customerAge"
        | "partnerAge"
        | "dependentChildrenUnder16"
        | "dependentChildrenAged16Plus",
      v: unknown
    ) => {
      if (typeof v === "number" && Number.isFinite(v)) {
        out[key] = v;
        return;
      }
      if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v);
        if (Number.isFinite(n)) out[key] = n;
      }
    };

    putStr("jointAccount", (base as any)?.jointAccount);
    putStr("customerEmploymentStatus", (base as any)?.customerEmploymentStatus);
    putStr("partnerEmploymentStatus", (base as any)?.partnerEmploymentStatus);
    putStr("haveChildrenUnder18", (base as any)?.haveChildrenUnder18);
    putStr("accommodationType", (base as any)?.accommodationType);
    putNum("customerAge", (base as any)?.customerAge);
    putNum("partnerAge", (base as any)?.partnerAge);
    putNum("dependentChildrenUnder16", (base as any)?.dependentChildrenUnder16);
    putNum(
      "dependentChildrenAged16Plus",
      (base as any)?.dependentChildrenAged16Plus
    );

    return out;
  }, [buildBaseVisibilityContext]);

  const triggersContext: TriggersContextShape = useMemo(() => {
    const incomeExceeded: Record<string, boolean> = {};
    const incomeMaxByField: Record<string, number> = {};
    const assetsExceeded: Record<string, boolean> = {};
    const assetsMaxByField: Record<string, number> = {};
    const expenseExceeded: Record<string, boolean> = {};
    const expenseMaxByField: Record<string, number> = {};
    const rootMaxByField: Record<string, number> = {};

    const jointAccount =
      (incomeRecord as any)?.jointAccount ??
      (profileData as any)?.jointAccount ??
      (customerMetadataForWarnings as any)?.jointAccount ??
      "";
    const withPartner = String(jointAccount || "").toUpperCase() === "Y";

    const depU16 =
      Number(
        (profileData as any)?.dependentChildrenUnder16 ??
          (customerMetadataForWarnings as any)?.dependentChildrenUnder16 ??
          0
      ) || 0;
    const dep16P =
      Number(
        (profileData as any)?.dependentChildrenAged16Plus ??
          (customerMetadataForWarnings as any)?.dependentChildrenAged16Plus ??
          0
      ) || 0;
    const numChildren = depU16 + dep16P;

    const readNumberMaybe = (v: unknown): number | undefined => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim() !== "") {
        const n = Number(v.replace(/,/g, ""));
        if (Number.isFinite(n)) return n;
      }
      return undefined;
    };

    try {
      (incomeTriggers as any[]).forEach((t: any) => {
        const rawKeys = [t?.fieldName, t?.columnName]
          .filter(
            (v: unknown) =>
              typeof v === "string" && (v as string).trim().length > 0
          )
          .map((v: string) => v.trim());
        if (rawKeys.length === 0) return;

        const rawUpperKeys = rawKeys
          .map((k) => normalizeTriggerFieldUpper(k))
          .filter(Boolean) as string[];
        if (rawUpperKeys.length === 0) return;

        const apiFieldName = (() => {
          const direct =
            typeof t?.fieldName === "string" ? t.fieldName.trim() : "";
          if (direct && ALL_API_AMOUNT_FIELDS.has(direct)) return direct;
          for (const upperKey of rawUpperKeys) {
            const hit =
              TRIGGER_KEY_TO_API_FIELD[upperKey] ??
              TRIGGER_KEY_TO_API_FIELD[upperKey.replace(/_/g, "")];
            if (hit) return hit;
          }
          const camel = upperSnakeToCamel(rawUpperKeys[0] as string);
          if (camel && ALL_API_AMOUNT_FIELDS.has(camel)) return camel;
          return direct || camel;
        })();
        if (!apiFieldName) return;

        const canonicalUpper = normalizeTriggerFieldUpper(
          toUpperSnake(apiFieldName)
        );
        const triggerKeyAliases = Array.from(
          new Set(
            [
              ...rawUpperKeys,
              ...rawUpperKeys.map((k) => k.replace(/_/g, "")),
              canonicalUpper,
              canonicalUpper ? canonicalUpper.replace(/_/g, "") : "",
            ].filter(Boolean)
          )
        );

        const incomeType = String(t?.incomeType ?? "").toLowerCase() as
          | "income"
          | "asset"
          | "expense"
          | "";
        const group: "income" | "expense" | "assets" =
          incomeType === "expense"
            ? "expense"
            : incomeType === "asset"
              ? "assets"
              : "income";

        const record =
          group === "expense"
            ? (expenseRecord as any) || {}
            : group === "assets"
              ? (assetRecord as any) || {}
              : (incomeRecord as any) || {};

        let numericVal = readNumberMaybe(record?.[apiFieldName]);

        if (group !== "assets") {
          const freqField = API_AMOUNT_TO_FREQUENCY_FIELD[apiFieldName];
          const freqValue = freqField ? record?.[freqField] : undefined;
          const monthlyVal = normalizeToMonthly(numericVal, freqValue);
          if (typeof monthlyVal === "number" && Number.isFinite(monthlyVal)) {
            numericVal = monthlyVal;
          }
        }

        const capUpper =
          withPartner && typeof (t as any)?.upperLimitJoint === "number"
            ? (t as any).upperLimitJoint
            : t?.upperLimit;

        let effectiveUpper: number | undefined = capUpper;
        if (triggerKeyAliases.includes("CHILD_BENEFIT")) {
          const eldest =
            typeof t?.eldestChild === "number" ? t.eldestChild : undefined;
          const additional =
            typeof t?.additionalChild === "number"
              ? t.additionalChild
              : undefined;
          if (
            eldest !== undefined &&
            additional !== undefined &&
            numChildren > 0
          ) {
            effectiveUpper = eldest + Math.max(0, numChildren - 1) * additional;
          }
        }

        if (typeof effectiveUpper === "number") {
          triggerKeyAliases.forEach((k) => {
            rootMaxByField[k] = effectiveUpper as number;
            if (group === "income")
              incomeMaxByField[k] = effectiveUpper as number;
            if (group === "assets")
              assetsMaxByField[k] = effectiveUpper as number;
            if (group === "expense")
              expenseMaxByField[k] = effectiveUpper as number;
          });
        }

        const belowLower =
          typeof t?.lowerLimit === "number" &&
          typeof numericVal === "number" &&
          Math.round(numericVal * 100) / 100 < Math.round(t.lowerLimit * 100) / 100;
        const aboveUpper =
          typeof effectiveUpper === "number" &&
          typeof numericVal === "number" &&
          Math.round(numericVal * 100) / 100 > Math.round(effectiveUpper * 100) / 100;
        const isExceeded = Boolean(belowLower || aboveUpper);

        if (isExceeded) {
          if (group === "income") {
            triggerKeyAliases.forEach((k) => {
              incomeExceeded[k] = true;
            });
          } else if (group === "assets") {
            triggerKeyAliases.forEach((k) => {
              assetsExceeded[k] = true;
            });
          } else if (group === "expense") {
            triggerKeyAliases.forEach((k) => {
              expenseExceeded[k] = true;
            });
          }
        }
      });
    } catch (e) {
      console.warn("Error computing triggers context:", e);
    }

    return {
      income: {
        exceeded: { ...incomeExceeded, ...assetsExceeded },
        maxByField: { ...incomeMaxByField, ...assetsMaxByField },
      },
      expense: { exceeded: expenseExceeded, maxByField: expenseMaxByField },
      maxByField: rootMaxByField,
    };
  }, [
    incomeTriggers,
    incomeRecord,
    expenseRecord,
    assetRecord,
    profileData,
    customerMetadataForWarnings,
  ]);

  // Section-level yes/no toggles used in the JSON schemas.
  // These must match the schema field names (not the backend API names) so that
  // the breakdown page can detect when a visibility rule is driven by a
  // section toggle (haveX) and apply special \"show even if toggle is off\"
  // logic when there is already answered data.
  const YES_NO_TOGGLE_VAR_NAMES: ReadonlySet<string> = new Set([
    "haveBenefits",
    "havePension",
    "haveOtherSources",
    "haveSavings",
    "haveLeisure",
    "haveHousekeeping",
    "havePersonalCosts",
    "haveHouseholdExpenses",
    "haveUtilities",
    "haveCareExpenses",
    "haveTransportExpenses",
    "haveSchoolExpenses",
    "havePensionInsurance",
    "haveProfessionalCosts",
    "haveOtherFixedExpenses",
    "haveDebts",
  ]);

  const buildCtxWithYesNoOverrides = useCallback(
    (ctx: Record<string, unknown>): Record<string, unknown> => {
      const next: Record<string, unknown> = { ...ctx };
      YES_NO_TOGGLE_VAR_NAMES.forEach((toggleName) => {
        next[toggleName] = true;
      });
      return next;
    },
    []
  );

  const visibilityExprUsesYesNoToggle = (expr: unknown): boolean => {
    if (Array.isArray(expr)) {
      return expr.some((item) => visibilityExprUsesYesNoToggle(item));
    }

    if (expr === null || typeof expr !== "object") {
      return false;
    }

    const obj = expr as Record<string, unknown>;
    const maybeVar = (obj as { var?: unknown })["var"];
    if (typeof maybeVar === "string") {
      const varName = maybeVar.includes(".")
        ? maybeVar.split(".").pop()
        : maybeVar;
      if (varName && YES_NO_TOGGLE_VAR_NAMES.has(varName)) {
        return true;
      }
    }

    const maybeArgs = (obj as { args?: unknown }).args;
    if (
      Array.isArray(maybeArgs) &&
      maybeArgs.some((arg) => visibilityExprUsesYesNoToggle(arg))
    ) {
      return true;
    }

    return Object.values(obj).some((value) =>
      visibilityExprUsesYesNoToggle(value)
    );
  };

  const getIncomeSchemaForGroup = useCallback(
    (groupKey: IncomeGroupKey): any | null => {
      switch (groupKey) {
        case "salary":
          return salarySchema;
        case "benefits":
          return benefitsSchema;
        case "pension":
          return pensionSchema;
        case "otherSources":
          return otherSourcesSchema;
        default:
          return null;
      }
    },
    []
  );

  const getFlexibleSchemaForGroup = useCallback(
    (groupKey: FlexibleGroupKey): any | null => {
      switch (groupKey) {
        case "leisure":
          return leisureSchema;
        case "housekeeping":
          return housekeepingSchema;
        case "personalCosts":
          return personalCostsSchema;
        case "savings":
          return savingsSchemaFlex;
        default:
          return null;
      }
    },
    []
  );

  const getFieldConfigFromSchema = useCallback(
    (schema: any, fieldName: string): any | null => {
      if (
        !schema ||
        !schema.sections ||
        !Array.isArray(schema.sections) ||
        !schema.sections[0]
      ) {
        return null;
      }
      const section = schema.sections[0];
      if (!section || !Array.isArray(section.fields)) {
        return null;
      }
      const field = section.fields.find(
        (f: any) => f && f.fieldName === fieldName
      );
      return field || null;
    },
    []
  );

  const isIncomeFieldVisibleBySchema = useCallback(
    (
      groupKey: IncomeGroupKey,
      schemaFieldName: string,
      baseCtx: Record<string, unknown>,
      categorySchemaData: Record<string, unknown> | null | undefined
    ): boolean => {
      const schema = getIncomeSchemaForGroup(groupKey);
      if (!schema) return true;
      const field = getFieldConfigFromSchema(schema, schemaFieldName);
      if (!field) return true;
      if (field.isHidden) return false;

      const ctx: Record<string, unknown> = {
        ...baseCtx,
        ...(categorySchemaData ?? {}),
      };

      if (field.visibleWhen) {
        const ctxWithOverrides = buildCtxWithYesNoOverrides(ctx);
        return evaluateVisibilityRule(
          field.visibleWhen as VisibilityNode,
          ctxWithOverrides
        );
      }
      return true;
    },
    [
      getIncomeSchemaForGroup,
      getFieldConfigFromSchema,
      buildCtxWithYesNoOverrides,
    ]
  );

  const isFlexibleFieldVisibleBySchema = useCallback(
    (
      groupKey: FlexibleGroupKey,
      schemaFieldName: string,
      baseCtx: Record<string, unknown>,
      categorySchemaData: Record<string, unknown> | null | undefined
    ): boolean => {
      // Special rule for savings: the breakdown page must not hide the savingsAmount
      // row purely because of the haveSavings toggle. If there is data mapped for
      // monthlySavingAmount, we always surface it here regardless of the toggle.
      if (groupKey === "savings" && schemaFieldName === "savingsAmount") {
        return true;
      }

      const schema = getFlexibleSchemaForGroup(groupKey);
      if (!schema) return true;
      const field = getFieldConfigFromSchema(schema, schemaFieldName);
      if (!field) return true;
      if (field.isHidden) return false;

      const ctx: Record<string, unknown> = {
        ...baseCtx,
        ...(categorySchemaData ?? {}),
      };

      if (field.visibleWhen) {
        const ctxWithOverrides = buildCtxWithYesNoOverrides(ctx);
        return evaluateVisibilityRule(
          field.visibleWhen as VisibilityNode,
          ctxWithOverrides
        );
      }
      return true;
    },
    [
      getFlexibleSchemaForGroup,
      getFieldConfigFromSchema,
      buildCtxWithYesNoOverrides,
    ]
  );

  const isFieldVisible = useCallback(
    (
      sectionId: "income" | "assets" | "fixedExpense" | "flexibleExpense",
      groupKey: string,
      _apiFieldName: string | null,
      schemaFieldName: string | null,
      _label: string
    ): boolean => {
      // Currently, visibility rules are driven almost entirely by the JSON schemas.
      // We only apply a few additional guard rails for child-related fields.
      const { total: childrenTotal } = getChildrenCounts();
      const schemaKey = (schemaFieldName || "").toLowerCase();

      // Income section rules
      if (sectionId === "income") {
        // For income, all visibility is controlled by the JSON schemas via the
        // SpecialScreen renderer on the Update Budget page. The breakdown page
        // must mirror that behaviour and therefore must not add any additional
        // show/hide conditions here. Every mapped income field that exists in the
        // API → schema mapping will be surfaced.
      }

      // Fixed expenses rules
      if (sectionId === "fixedExpense") {
        // All fixed expenses fields (household, utilities, care, transport, school,
        // pension & insurance, professionals, other sources) follow the JSON schema
        // visibility logic via the dynamic engine on the Update Budget page.
        // The breakdown page must not introduce extra show/hide conditions that could
        // hide fields which are visible (and answerable) in the schema-driven screen,
        // so we deliberately do not apply any additional filtering here.
        if (groupKey === "school" && childrenTotal <= 0) {
          // if there is no children then we should not consider school related questions  while doing calculation
          return false;
        }
      }

      // Flexible expense rules
      if (sectionId === "flexibleExpense") {
        if (groupKey === "leisure" && schemaKey === "pocketmoney") {
          return childrenTotal > 0;
        }
        if (
          groupKey === "housekeeping" &&
          schemaKey === "nappiesandbabyitems"
        ) {
          return childrenTotal > 0;
        }
        // Default visible
        return true;
      }

      // Assets and other sections default visible
      return true;
    },
    [getChildrenCounts]
  );

  // Income items derived from API
  const incomeItems: BudgetItem[] = useMemo(() => {
    const toCurrency = (val: any): string => {
      if (val === undefined || val === null || val === "" || isNaN(Number(val)))
        return "-";
      const num = typeof val === "string" ? parseFloat(val) : Number(val);
      return `£${num.toFixed(2)}`;
    };
    const toTitle = (s?: string): string =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

    const salary = incomeRecord
      ? mapIncomeDataToSchema(
          incomeRecord as any,
          incomeNotesRecord as any,
          "salary"
        )
      : ({} as any);
    const benefits = incomeRecord
      ? mapIncomeDataToSchema(
          incomeRecord as any,
          incomeNotesRecord as any,
          "benefits"
        )
      : ({} as any);
    const pension = incomeRecord
      ? mapIncomeDataToSchema(
          incomeRecord as any,
          incomeNotesRecord as any,
          "pension"
        )
      : ({} as any);
    const otherSources = incomeRecord
      ? mapIncomeDataToSchema(
          incomeRecord as any,
          incomeNotesRecord as any,
          "otherSources"
        )
      : ({} as any);

    console.log("Mapped income data:", {
      salary,
      benefits,
      pension,
      otherSources,
    });

    console.log("Raw incomeRecord:", incomeRecord);
    console.log("Salary object after mapping:", salary);
    console.log("Salary takeHomeSalary:", salary?.takeHomeSalary);

    type FieldDef = {
      label: string;
      groupKey: "salary" | "benefits" | "pension" | "otherSources";
      schemaField: string;
      apiField: string | null;
      amount?: any;
      frequency?: any;
    };

    const defs: FieldDef[] = [
      // Salary
      {
        label: "Salary",
        groupKey: "salary",
        schemaField: "takeHomeSalary",
        apiField:
          getApiFieldNameFromSchemaField("takeHomeSalary", "salary") || null,
        amount: salary?.takeHomeSalary?.amount,
        frequency: salary?.takeHomeSalary?.frequency,
      },
      {
        label: "Partner's salary",
        groupKey: "salary",
        schemaField: "partnerTakeHomeSalary",
        apiField:
          getApiFieldNameFromSchemaField("partnerTakeHomeSalary", "salary") ||
          null,
        amount: salary?.partnerTakeHomeSalary?.amount,
        frequency: salary?.partnerTakeHomeSalary?.frequency,
      },
      {
        label: "Other salary source",
        groupKey: "salary",
        schemaField: "otherSalarySource",
        apiField:
          getApiFieldNameFromSchemaField("otherSalarySource", "salary") || null,
        amount: salary?.otherSalarySource?.amount,
        frequency: salary?.otherSalarySource?.frequency,
      },
      // Benefits
      {
        label: "Universal credit",
        groupKey: "benefits",
        schemaField: "universalCredit",
        apiField:
          getApiFieldNameFromSchemaField("universalCredit", "benefits") || null,
        amount: benefits?.universalCredit?.amount,
        frequency: benefits?.universalCredit?.frequency,
      },
      {
        label: "Jobseeker's Allowance (contribution-based)",
        groupKey: "benefits",
        schemaField: "jobSeekerAllowanceContribute",
        apiField:
          getApiFieldNameFromSchemaField(
            "jobSeekerAllowanceContribute",
            "benefits"
          ) || null,
        amount: benefits?.jobSeekerAllowanceContribute?.amount,
        frequency: benefits?.jobSeekerAllowanceContribute?.frequency,
      },
      {
        label: "Child benefit",
        groupKey: "benefits",
        schemaField: "childBenefit",
        apiField:
          getApiFieldNameFromSchemaField("childBenefit", "benefits") || null,
        amount: benefits?.childBenefit?.amount,
        frequency: benefits?.childBenefit?.frequency,
      },
      {
        label: "ESA or SSP",
        groupKey: "benefits",
        schemaField: "empsupportallowancestatutory",
        apiField:
          getApiFieldNameFromSchemaField(
            "empsupportallowancestatutory",
            "benefits"
          ) || null,
        amount: benefits?.empsupportallowancestatutory?.amount,
        frequency: benefits?.empsupportallowancestatutory?.frequency,
      },
      {
        label: "Disability benefits (DLA/PIP/AA)",
        groupKey: "benefits",
        schemaField: "disabilityAllowance",
        apiField:
          getApiFieldNameFromSchemaField("disabilityAllowance", "benefits") ||
          null,
        amount: benefits?.disabilityAllowance?.amount,
        frequency: benefits?.disabilityAllowance?.frequency,
      },
      {
        label: "Carer's allowance",
        groupKey: "benefits",
        schemaField: "carerAllowance",
        apiField:
          getApiFieldNameFromSchemaField("carerAllowance", "benefits") || null,
        amount: benefits?.carerAllowance?.amount,
        frequency: benefits?.carerAllowance?.frequency,
      },
      {
        label: "Housing Benefit Local Allowance",
        groupKey: "benefits",
        schemaField: "housingBenefitLocalAllowance",
        apiField:
          getApiFieldNameFromSchemaField(
            "housingBenefitLocalAllowance",
            "benefits"
          ) || null,
        amount: benefits?.housingBenefitLocalAllowance?.amount,
        frequency: benefits?.housingBenefitLocalAllowance?.frequency,
      },
      {
        label: "Council Tax Support",
        groupKey: "benefits",
        schemaField: "councilTaxSupport",
        apiField:
          getApiFieldNameFromSchemaField("councilTaxSupport", "benefits") ||
          null,
        amount: benefits?.councilTaxSupport?.amount,
        frequency: benefits?.councilTaxSupport?.frequency,
      },
      {
        label: "Student loans & grants",
        groupKey: "benefits",
        schemaField: "studentLoansAndGrants",
        apiField:
          getApiFieldNameFromSchemaField("studentLoansAndGrants", "benefits") ||
          null,
        amount: benefits?.studentLoansAndGrants?.amount,
        frequency: benefits?.studentLoansAndGrants?.frequency,
      },
      {
        label: "Other benefits",
        groupKey: "benefits",
        schemaField: "otherBenefits",
        apiField:
          getApiFieldNameFromSchemaField("otherBenefits", "benefits") || null,
        amount: benefits?.otherBenefits?.amount,
        frequency: benefits?.otherBenefits?.frequency,
      },
      // Pension
      {
        label: "State pension",
        groupKey: "pension",
        schemaField: "statePension",
        apiField:
          getApiFieldNameFromSchemaField("statePension", "pension") || null,
        amount: pension?.statePension?.amount,
        frequency: pension?.statePension?.frequency,
      },
      {
        label: "Private pension",
        groupKey: "pension",
        schemaField: "privatePension",
        apiField:
          getApiFieldNameFromSchemaField("privatePension", "pension") || null,
        amount: pension?.privatePension?.amount,
        frequency: pension?.privatePension?.frequency,
      },
      {
        label: "Pension Credit",
        groupKey: "pension",
        schemaField: "pensionCredit",
        apiField:
          getApiFieldNameFromSchemaField("pensionCredit", "pension") || null,
        amount: pension?.pensionCredit?.amount,
        frequency: pension?.pensionCredit?.frequency,
      },
      {
        label: "Pension Others",
        groupKey: "pension",
        schemaField: "pensionOthers",
        apiField:
          getApiFieldNameFromSchemaField("pensionOthers", "pension") || null,
        amount: pension?.pensionOthers?.amount,
        frequency: pension?.pensionOthers?.frequency,
      },
      // Other sources
      {
        label: "Child Maintenance or Support",
        groupKey: "otherSources",
        schemaField: "childMaintenanceOrSupport",
        apiField:
          getApiFieldNameFromSchemaField(
            "childMaintenanceOrSupport",
            "otherSources"
          ) || null,
        amount: otherSources?.childMaintenanceOrSupport?.amount,
        frequency: otherSources?.childMaintenanceOrSupport?.frequency,
      },
      {
        label: "Borders or Lodgers",
        groupKey: "otherSources",
        schemaField: "bordersAndLodger",
        apiField:
          getApiFieldNameFromSchemaField("bordersAndLodger", "otherSources") ||
          null,
        amount: otherSources?.bordersAndLodger?.amount,
        frequency: otherSources?.bordersAndLodger?.frequency,
      },
      {
        label: "Non-Dependant Contributions",
        groupKey: "otherSources",
        schemaField: "nonDependantContributions",
        apiField:
          getApiFieldNameFromSchemaField(
            "nonDependantContributions",
            "otherSources"
          ) || null,
        amount: otherSources?.nonDependantContributions?.amount,
        frequency: otherSources?.nonDependantContributions?.frequency,
      },
      {
        label: "Money from Other sources",
        groupKey: "otherSources",
        schemaField: "moneyFromOtherSources",
        apiField:
          getApiFieldNameFromSchemaField(
            "moneyFromOtherSources",
            "otherSources"
          ) || null,
        amount: otherSources?.moneyFromOtherSources?.amount,
        frequency: otherSources?.moneyFromOtherSources?.frequency,
      },
    ];

    const baseCtx = buildBaseVisibilityContext();

    const filtered = defs.filter((def) => {
      if (!def.schemaField) return true;
      const categoryData =
        def.groupKey === "salary"
          ? salary
          : def.groupKey === "benefits"
            ? benefits
            : def.groupKey === "pension"
              ? pension
              : otherSources;
      return isIncomeFieldVisibleBySchema(
        def.groupKey,
        def.schemaField,
        baseCtx,
        categoryData as Record<string, unknown>
      );
    });

    const items: BudgetItem[] = filtered.map((def) => ({
      label: def.label,
      amount: toCurrency(def.amount),
      frequency: toTitle(def.frequency || ""),
      editable: true,
    }));

    return items;
  }, [
    incomeRecord,
    incomeNotesRecord,
    buildBaseVisibilityContext,
    isIncomeFieldVisibleBySchema,
  ]);

  // Income stats (questions, answered, monthly total)
  const incomeStats: SectionStats = useMemo(() => {
    const total = incomeItems.length;
    let answered = 0;
    let monthlyTotal = 0;
    incomeItems.forEach((it) => {
      const amt = parseCurrencyAmount(it.amount);
      if (amt !== null) {
        answered += 1;
        monthlyTotal += toMonthlyAmount(amt, it.frequency || "monthly");
      }
    });
    return { total, answered, monthlyTotal };
  }, [incomeItems]);

  // Asset items derived from API
  const assetItems: BudgetItem[] = useMemo(() => {
    const toCurrency = (val: any): string => {
      if (val === undefined || val === null || val === "" || isNaN(Number(val)))
        return "-";
      const num = typeof val === "string" ? parseFloat(val) : Number(val);
      return `£${num.toFixed(2)}`;
    };

    const properties = assetRecord
      ? mapAssetDataToSchema(
          assetRecord as any,
          assetNotesRecord as any,
          "properties"
        )
      : ({} as any);
    const otherAssets = assetRecord
      ? mapAssetDataToSchema(
          assetRecord as any,
          assetNotesRecord as any,
          "otherAssets"
        )
      : ({} as any);

    const items: BudgetItem[] = [
      // Properties
      {
        label: "Total value of all your properties",
        amount: toCurrency(properties?.propertiesTotalValue?.amount),
        frequency: "",
        editable: true,
      },
      {
        label: "Outstanding on all mortgages",
        amount: toCurrency(properties?.propertiesMortgageOutstanding?.amount),
        frequency: "",
        editable: true,
      },
      {
        label: "Loans secured against your property",
        amount: toCurrency(
          properties?.propertiesSecuredLoanOutstanding?.amount
        ),
        frequency: "",
        editable: true,
      },
      // Other assets
      {
        label: "Vehicle(s) worth",
        amount: toCurrency(otherAssets?.valueOfVehicles?.amount),
        frequency: "",
        editable: true,
      },
      {
        label: "Money in savings",
        amount: toCurrency(otherAssets?.assetSavings?.amount),
        frequency: "",
        editable: true,
      },
      {
        label: "Other assets worth",
        amount: toCurrency(otherAssets?.otherAssets?.amount),
        frequency: "",
        editable: true,
      },
    ];
    return items;
  }, [assetRecord, assetNotesRecord]);

  // Asset stats (use total value instead of monthly)
  const assetStats = useMemo(() => {
    const total = assetItems.length;
    // Count answered items (any item with a parsable numeric amount)
    const answered = assetItems.reduce((count, it) => {
      const amt = parseCurrencyAmount(it.amount);
      return count + (amt !== null ? 1 : 0);
    }, 0);

    // Legacy-equivalent rule:
    // Assets total = property value - mortgage outstanding - secured loans
    //                + vehicle(s) worth + savings + other assets
    const getAmountByLabel = (label: string): number => {
      const found = assetItems.find((it) => it.label === label);
      const n = found ? parseCurrencyAmount(found.amount) : null;
      return n !== null ? n : 0;
    };

    const propertyTotal = getAmountByLabel(
      "Total value of all your properties"
    );
    const mortgageOutstanding = getAmountByLabel(
      "Outstanding on all mortgages"
    );
    const securedLoans = getAmountByLabel(
      "Loans secured against your property"
    );
    const vehiclesWorth = getAmountByLabel("Vehicle(s) worth");
    const savings = getAmountByLabel("Money in savings");
    const otherAssets = getAmountByLabel("Other assets worth");

    const totalValue =
      propertyTotal -
      mortgageOutstanding -
      securedLoans +
      vehiclesWorth +
      savings +
      otherAssets;

    return { total, answered, totalValue } as {
      total: number;
      answered: number;
      totalValue: number;
    };
  }, [assetItems]);

  // Fixed expenses items derived from API (auto-generated from schema mappings) with visibility
  const fixedExpenseList = useMemo(() => {
    const toCurrency = (val: any): string => {
      if (val === undefined || val === null || val === "" || isNaN(Number(val)))
        return "-";
      const num = typeof val === "string" ? parseFloat(val) : Number(val);
      return `£${num.toFixed(2)}`;
    };
    const toTitle = (s?: string): string =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

    // NI label transformation helper (council tax → council rates)
    const transformLabelForNI = (labelText: string): string => {
      if (!isNorthernIreland) return labelText;
      return labelText
        .replace(/Council Tax/g, 'Council Rates')
        .replace(/council tax/g, 'council rates')
        .replace(/Council tax/g, 'Council rates')
        .replace(/COUNCIL TAX/g, 'COUNCIL RATES');
    };

    // Check if water bill data exists (for individual field visibility)
    const hasExistingWaterBillData = (): boolean => {
      if (!expenseRecord) return false;
      const waterBill = (expenseRecord as any)?.waterBill;
      return typeof waterBill === 'number' && waterBill > 0;
    };

    // Check if water waste data exists (for individual field visibility)
    const hasExistingWaterWasteData = (): boolean => {
      if (!expenseRecord) return false;
      const waterWaste = (expenseRecord as any)?.waterWaste;
      return typeof waterWaste === 'number' && waterWaste > 0;
    };

    type FixedCat =
      | "household"
      | "utilities"
      | "care"
      | "transport"
      | "school"
      | "pensionInsurance"
      | "professionals"
      | "otherSourcesFixed";
    const innerKeyMap: Record<FixedCat, string> = {
      household: "household",
      utilities: "utilities",
      care: "care",
      transport: "transport",
      school: "school",
      pensionInsurance: "pension-insurance",
      professionals: "professionals",
      otherSourcesFixed: "other-sources",
    };

    const categories: FixedCat[] = [
      "household",
      "utilities",
      "care",
      "transport",
      "school",
      "pensionInsurance",
      "professionals",
      "otherSourcesFixed",
    ];
    const rows: Array<{
      item: BudgetItem;
      innerKey: string;
      groupKey: (typeof categories)[number];
      fieldKey: string;
    }> = [];

    if (!expenseRecord) return rows;

    // Pre-compute water data existence for NI users (individual checks)
    const hasWaterBill = hasExistingWaterBillData();
    const hasWaterWaste = hasExistingWaterWasteData();

    categories.forEach((cat) => {
      const mapped =
        mapExpenseDataToSchema(
          expenseRecord as any,
          expenseNotesRecord as any,
          cat as any
        ) || {};
      const mappingObj = (BUDGET_FIELD_MAPPINGS as any)[cat] || {};
      Object.keys(mappingObj).forEach((schemaFieldName) => {
        // NI-specific: Hide each water field individually if no data for that field
        if (isNorthernIreland) {
          if (schemaFieldName === 'water' && !hasWaterBill) {
            return; // Skip water supply if no water bill data
          }
          if (schemaFieldName === 'waterWaste' && !hasWaterWaste) {
            return; // Skip water waste if no water waste data
          }
        }

        const field = (mapped as any)[schemaFieldName] || {};
        const fieldConfig = (mappingObj as any)[schemaFieldName] || {};
        const apiField = getApiFieldNameFromSchemaField(
          schemaFieldName,
          cat as any
        );
        const rawLabel =
          fieldConfig.label ||
          (apiField ? getFieldLabelByApiName(apiField) : schemaFieldName);
        // Apply NI label transformation (council tax → council rates)
        let label = rawLabel
          ? rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1)
          : schemaFieldName;
        label = transformLabelForNI(label);

        // For NI users with existing water data, append "(not applicable to Northern Ireland)"
        if (isNorthernIreland) {
          if (schemaFieldName === 'water' && hasWaterBill) {
            if (!label.includes('(not applicable to Northern Ireland)')) {
              label = `${label} (not applicable to Northern Ireland)`;
            }
          }
          if (schemaFieldName === 'waterWaste' && hasWaterWaste) {
            if (!label.includes('(not applicable to Northern Ireland)')) {
              label = `${label} (not applicable to Northern Ireland)`;
            }
          }
        }

        if (
          !isFieldVisible(
            "fixedExpense",
            cat,
            apiField || null,
            schemaFieldName,
            label
          )
        ) {
          return;
        }

        const amount = toCurrency(field?.amount);
        const frequency = toTitle(field?.frequency || "");

        rows.push({
          item: {
            label,
            amount,
            frequency,
            editable: true,
          },
          innerKey: innerKeyMap[cat],
          groupKey: cat,
          fieldKey: schemaFieldName,
        });
      });
    });

    return rows;
  }, [expenseRecord, expenseNotesRecord, isFieldVisible, profileData, isNorthernIreland]);

  // Fixed expenses stats
  const fixedStats: SectionStats = useMemo(() => {
    const total = fixedExpenseList.length;
    let answered = 0;
    let monthlyTotal = 0;
    fixedExpenseList.forEach((row) => {
      const amt = parseCurrencyAmount(row.item.amount);
      if (amt !== null) {
        answered += 1;
        monthlyTotal += toMonthlyAmount(amt, row.item.frequency || "monthly");
      }
    });
    return { total, answered, monthlyTotal };
  }, [fixedExpenseList]);

  // Flexible expense items derived from API (leisure/housekeeping/personal-costs from expense, savings from income) with visibility
  const flexibleExpenseList = useMemo(() => {
    const toCurrency = (val: any): string => {
      if (val === undefined || val === null || val === "" || isNaN(Number(val)))
        return "-";
      const num = typeof val === "string" ? parseFloat(val) : Number(val);
      return `£${num.toFixed(2)}`;
    };
    const toTitle = (s?: string): string =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

    type FlexCat = "leisure" | "housekeeping" | "personalCosts";
    const innerKeyMap: Record<string, string> = {
      leisure: "leisure",
      housekeeping: "housekeeping",
      personalCosts: "personal-costs",
      savings: "savings",
    };

    const categories: FlexCat[] = ["leisure", "housekeeping", "personalCosts"];
    const rows: Array<{
      item: BudgetItem;
      innerKey: string;
      groupKey: string;
      fieldKey: string;
    }> = [];

    const baseCtx = buildBaseVisibilityContext();

    // From expenses
    if (expenseRecord) {
      categories.forEach((cat) => {
        const mapped =
          mapExpenseDataToSchema(
            expenseRecord as any,
            expenseNotesRecord as any,
            cat as any
          ) || {};
        const mappingObj = (BUDGET_FIELD_MAPPINGS as any)[cat] || {};
        Object.keys(mappingObj).forEach((schemaFieldName) => {
          // Handle special nested structure for gamblingFromHobbiesLeisure
          let field = (mapped as any)[schemaFieldName] || {};
          if (
            cat === "leisure" &&
            schemaFieldName === "gamblingFromHobbiesLeisure"
          ) {
            // Check if data exists in nested structure under hobbiesLeisureSports
            const hobbiesData = (mapped as any)["hobbiesLeisureSports"];
            if (hobbiesData && hobbiesData["gamblingFromHobbiesLeisure"]) {
              field = hobbiesData["gamblingFromHobbiesLeisure"];
            }
          }

          // Legacy rule: "Gambling spending" visibility depends on "Hobbies, leisure and sports".
          // Only show and calculate gambling spending when the base "Hobbies, leisure and sports"
          // amount is greater than zero.
          if (
            cat === "leisure" &&
            schemaFieldName === "gamblingFromHobbiesLeisure"
          ) {
            const hobbiesField = (mapped as any)["hobbiesLeisureSports"] || {};
            const baseAmountRaw =
              typeof hobbiesField === "object" && hobbiesField !== null
                ? (hobbiesField as any).amount
                : hobbiesField;
            const baseAmountNum = Number(baseAmountRaw);
            if (!Number.isFinite(baseAmountNum) || baseAmountNum <= 0) {
              return;
            }
          }

          const fieldConfig = (mappingObj as any)[schemaFieldName] || {};
          const apiField = getApiFieldNameFromSchemaField(
            schemaFieldName,
            cat as any
          );
          const rawLabel =
            fieldConfig.label ||
            (apiField ? getFieldLabelByApiName(apiField) : schemaFieldName);
          const label = rawLabel
            ? rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1)
            : schemaFieldName;

          if (
            !isFlexibleFieldVisibleBySchema(
              cat as FlexibleGroupKey,
              schemaFieldName,
              baseCtx,
              mapped as Record<string, unknown>
            )
          ) {
            return;
          }

          const amount = toCurrency(field?.amount);
          const frequency = toTitle(field?.frequency || "");

          rows.push({
            item: {
              label,
              amount,
              frequency,
              editable: true,
            },
            innerKey: innerKeyMap[cat] || (cat as string),
            groupKey: cat,
            fieldKey: schemaFieldName,
          });
        });
      });
    }

    // Savings from income
    if (incomeRecord) {
      const savingsMapped =
        mapIncomeDataToSchema(
          incomeRecord as any,
          incomeNotesRecord as any,
          "savings" as any
        ) || {};
      const savingsField = (savingsMapped as any)?.savingsAmount || {};

      const savingsVisible = isFlexibleFieldVisibleBySchema(
        "savings",
        "savingsAmount",
        baseCtx,
        savingsMapped as Record<string, unknown>
      );

      if (savingsVisible) {
        let label = getFieldLabelByApiName("monthlySavingAmount");
        label = label
          ? label.charAt(0).toUpperCase() + label.slice(1)
          : "Savings";
        
        // For NI users, append Child Trust Fund note to savings label
        if (isNorthernIreland && !label.includes('Child Trust Fund')) {
          label = `Child Trust Funds`;
        }

        rows.push({
          item: {
            label,
            amount: toCurrency(savingsField.amount),
            frequency: "",
            editable: true,
          },
          innerKey: innerKeyMap["savings"] || "savings",
          groupKey: "savings",
          fieldKey: "savingsAmount",
        });
      }
    }

    return rows;
  }, [
    expenseRecord,
    expenseNotesRecord,
    incomeRecord,
    incomeNotesRecord,
    buildBaseVisibilityContext,
    isFlexibleFieldVisibleBySchema,
    isNorthernIreland,
  ]);

  // Flexible expense stats (Savings is treated as part of flexible expenses)
  const flexibleStats: SectionStats = useMemo(() => {
    const total = flexibleExpenseList.length;
    let answered = 0;
    let monthlyTotal = 0;
    flexibleExpenseList.forEach((row) => {
      const amt = parseCurrencyAmount(row.item.amount);
      if (amt !== null) {
        answered += 1;
        // Exclude Monthly saving amount from the flexible expenses monthly total
        // Also exclude Gambling spending (schema field: gamblingFromHobbiesLeisure) from the total
        if (
          row.groupKey !== "savings" &&
          !(
            row.groupKey === "leisure" &&
            row.fieldKey === "gamblingFromHobbiesLeisure"
          )
        ) {
          monthlyTotal += toMonthlyAmount(amt, row.item.frequency || "monthly");
        }
      }
    });
    return { total, answered, monthlyTotal };
  }, [flexibleExpenseList]);

  // Mirror savings warning from schema: show when haveSavings is true AND savingsAmount.amount > 25
  const savingsLimitExceeded = useMemo(() => {
    try {
      if (!incomeRecord) return false;
      const mapped = (mapIncomeDataToSchema(
        incomeRecord as any,
        incomeNotesRecord as any,
        "savings" as any
      ) || {}) as any;
      const haveSavings = mapped?.haveSavings === true;
      const rawAmount = mapped?.savingsAmount?.amount;
      let amountNum = 0;
      if (typeof rawAmount === "number") {
        amountNum = rawAmount;
      } else if (typeof rawAmount === "string") {
        const parsed = Number(String(rawAmount).replace(/,/g, ""));
        if (Number.isFinite(parsed)) amountNum = parsed;
      }
      return haveSavings && amountNum > 25;
    } catch {
      return false;
    }
  }, [incomeRecord, incomeNotesRecord]);

  // Debt Administration Fee from expense data
  useEffect(() => {
    if (expenseRecord) {
      const toCurrency = (val: any): string => {
        if (
          val === undefined ||
          val === null ||
          val === "" ||
          isNaN(Number(val))
        )
          return "-";
        const num = typeof val === "string" ? parseFloat(val) : Number(val);
        return `£${num.toFixed(2)}`;
      };
      const toTitle = (s?: string): string =>
        s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

      const adminData = mapExpenseDataToSchema(
        expenseRecord as any,
        expenseNotesRecord as any,
        "debtsAdministration"
      );
      const adminFeeField = (adminData as any)?.administrationFee;

      // Check if hasDebtAdminFee is 'Y' - if so, show the field even if amount is 0 or missing
      const hasDebtAdminFee = (expenseRecord as any)?.hasDebtAdminFee;
      const rawDebtAdminFee = (expenseRecord as any)?.debtAdminFee;
      const rawDebtAdminFeeMode = (expenseRecord as any)?.debtAdminFeeMode;

      // Show if: hasDebtAdminFee is 'Y', OR if there's an amount value (even if 0)
      const shouldShowAdminFee =
        hasDebtAdminFee === "Y" ||
        hasDebtAdminFee === "y" ||
        (adminFeeField && adminFeeField.amount) ||
        (rawDebtAdminFee !== undefined && rawDebtAdminFee !== null);

      if (shouldShowAdminFee) {
        // Prefer mapped field, fallback to raw values
        const amount = adminFeeField?.amount || rawDebtAdminFee;
        const frequency = adminFeeField?.frequency || rawDebtAdminFeeMode;

        setDebtAdminFee({
          amount: amount ? toCurrency(amount) : "-",
          frequency: toTitle(frequency || ""),
        });
      } else {
        setDebtAdminFee(null);
      }
    } else {
      setDebtAdminFee(null);
    }
  }, [expenseRecord, expenseNotesRecord]);

  // Debts stats (priority + non-priority + admin fee)
  const debtsStats: SectionStats = useMemo(() => {
    const all = [...priorityDebts, ...nonPriorityDebts];
    let total = all.length;
    let answered = 0;
    let monthlyTotal = 0;

    // Add admin fee to stats if present
    // we should not calculate debt admin fee in stats calculation
    // issue sheet mentioned Debt admin fee should not be calculated and  displayed in the header - 352
    // if (debtAdminFee) {
    //   total += 1;
    //   const adminAmt = parseCurrencyAmount(debtAdminFee.amount);
    //   if (adminAmt !== null) {
    //     answered += 1;
    //     monthlyTotal += toMonthlyAmount(adminAmt, (debtAdminFee.frequency || 'monthly'));
    //   }
    // }

    all.forEach((d) => {
      // Prefer repayment amount for budgeting stats; fall back to amount owed if repayment is missing
      const rawAmount = d.repaymentAmount || d.amountOwed;
      const amt = parseCurrencyAmount(rawAmount);
      if (amt !== null) {
        answered += 1;
        monthlyTotal += toMonthlyAmount(amt, d.frequency || "monthly");
      }
    });
    return { total, answered, monthlyTotal };
  }, [priorityDebts, nonPriorityDebts, debtAdminFee]);

  // ---- Subtitles --------------------------------------------------------------
  const incomeSubtitle = useMemo(
    () => `${incomeStats.answered} of ${incomeStats.total} answered`,
    [incomeStats]
  );

  const assetsSubtitle = useMemo(
    () => `${assetStats.answered} of ${assetStats.total} answered`,
    [assetStats, calculatedTotals?.totalAssets]
  );

  const fixedSubtitle = useMemo(
    () => `${fixedStats.answered} of ${fixedStats.total} answered`,
    [fixedStats]
  );

  const flexibleSubtitle = useMemo(
    () => `${flexibleStats.answered} of ${flexibleStats.total} answered`,
    [flexibleStats]
  );

  const debtsSubtitle = useMemo(
    () =>
      `${debtsStats.total} ${debtsStats.total === 1 ? "debt" : "debts"} added`,
    [debtsStats]
  );

  // Summary cards data (dynamic)
  const summaryCards: SummaryCard[] = useMemo(() => {
    const makeIconBadge = (imageSrc: string) => (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={imageSrc} alt="" style={{ width: 20, height: 20 }} />
      </Box>
    );

    return [
      {
        id: "income",
        icon: makeIconBadge("/assets/budgetBreakdown/Income.svg"),
        label: "Income",
        frequency: "Monthly",
        amount: formatGBP(incomeStats.monthlyTotal || 0),
        status: `${incomeStats.answered} of ${incomeStats.total} answered`,
        color: "#3B82F6",
        backgroundColor: "#DBEAFE",
        backgroundGradient:
          "linear-gradient(135deg, #E0F2FE 0%, #EFF6FF 70.71%)",
      },
      {
        id: "assets",
        icon: makeIconBadge("/assets/budgetBreakdown/Assets.svg"),
        label: "Assets",
        frequency: "Total",
        amount: formatGBP(
          (calculatedTotals?.totalAssets ?? (assetStats as any).totalValue) || 0
        ),
        status: `${assetStats.answered} of ${assetStats.total} answered`,
        color: "#8B5CF6",
        backgroundColor: "#EDE9FE",
        backgroundGradient:
          "linear-gradient(135deg, #F3E8FF 0%, #FAF5FF 70.71%)",
      },
      {
        id: "fixedExpenses",
        icon: makeIconBadge("/assets/budgetBreakdown/Fixed.svg"),
        label: "Fixed expenses",
        frequency: "Monthly",
        amount: formatGBP(fixedStats.monthlyTotal || 0),
        status: `${fixedStats.answered} of ${fixedStats.total} answered`,
        color: "#10B981",
        backgroundColor: "#D1FAE5",
        backgroundGradient:
          "linear-gradient(135deg, #D1FAE5 0%, #F0FDF4 70.71%)",
      },
      {
        id: "flexibleExpenses",
        icon: makeIconBadge("/assets/budgetBreakdown/Flexible.svg"),
        label: "Flexible expenses",
        frequency: "Monthly",
        amount: formatGBP(flexibleStats.monthlyTotal || 0),
        status: `${flexibleStats.answered} of ${flexibleStats.total} answered`,
        color: "#06B6D4",
        backgroundColor: "#CFFAFE",
        backgroundGradient:
          "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 70.71%)",
      },
      {
        id: "debts",
        icon: makeIconBadge("/assets/budgetBreakdown/Debts.svg"),
        label: "Repayment debts",
        frequency: "Total",
        amount: formatGBP(debtsStats.monthlyTotal || 0),
        status: `${debtsStats.total} Debts added`,
        color: "#F59E0B",
        backgroundColor: "#FEF3C7",
        backgroundGradient:
          "linear-gradient(135deg, #FFF7ED 0%, #FFF7ED 70.71%)",
      },
    ];
  }, [incomeStats, assetStats, fixedStats, flexibleStats, debtsStats]);

  // Removed: Don't fetch on every location change
  // Data will be fetched on mount and when explicitly needed (e.g., after edits)

  // Handle showSummary URL parameter on page load
  useEffect(() => {
    if (!loading && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const showSummary = params.get("showSummary");

      if (showSummary === "true") {
        setIsReviewBudgetDialogOpen(true);

        // Clear the parameter from URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("showSummary");
        window.history.replaceState({}, "", newUrl.toString());
      }
    }
  }, [loading]);

  const normalisedCount = useMemo(
    () => normaliseCount(triggersCount),
    [triggersCount]
  );

  useEffect(() => {
    console.log("triggersCount", triggersCount);

    if (triggersCount == -1) {
      return;
    }

    if (window.location.href.includes("showSummary")) {
      if (triggersCount > 0) {
        setSmartReviewOpen(true);
      }
    }
  }, [triggersCount]);

  // Handle URL query parameter on page load
  useEffect(() => {
    if (!loading) {
      const params = new URLSearchParams(window.location.search);
      const section = params.get("section");

      if (section && expandedSections.hasOwnProperty(section)) {
        // Map section ID to card ID
        const sectionToCardMap: Record<string, string> = {
          income: "income",
          assets: "assets",
          fixedExpense: "fixedExpenses",
          flexibleExpense: "flexibleExpenses",
          debts: "debts",
        };

        const cardId = sectionToCardMap[section];
        if (cardId) {
          setActiveCardId(cardId);
        }

        // Collapse all sections first, then expand only the target section
        setExpandedSections({
          income: false,
          assets: false,
          fixedExpense: false,
          flexibleExpense: false,
          debts: false,
          [section]: true,
        });

        // Wait for section to expand, then scroll within the correct container
        scheduleScrollToSection(section, 600);
      }
    }
  }, [loading, scheduleScrollToSection]);

  // Smart refetch on tab focus - only if data is stale (older than STALE_TIME)
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const requestRefresh = () => {
      // If the initial load failed and the user is seeing the blocking screen,
      // do not auto-refresh in the background (it would spam retries/banners).
      if (initialLoadFailed) return;
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimestampRef.current;

      // Only fetch if data is stale (older than STALE_TIME)
      if (timeSinceLastFetch > STALE_TIME) {
        console.log("🔄 Data is stale, refetching...", {
          timeSinceLastFetch: Math.round(timeSinceLastFetch / 1000) + "s",
        });
        fetchRef.current();
      } else {
        console.log("✅ Data is fresh, skipping fetch", {
          timeSinceLastFetch: Math.round(timeSinceLastFetch / 1000) + "s",
        });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestRefresh();
      }
    };

    // Only listen to visibility change, not focus (less aggressive)
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [STALE_TIME, initialLoadFailed]);

  // Setup fetch ref and perform initial fetch on mount
  useEffect(() => {
    fetchRef.current = () => {
      fetchBudgetData();
    };
    // Listen for debts changes triggered by inline arrears dialog saves
    const onDebtsChanged = () => {
      // Force refresh to reflect newly created/updated debts immediately
      fetchBudgetData(true);
    };
    if (typeof window !== "undefined") {
      window.addEventListener(
        "iehub-debts-changed",
        onDebtsChanged as EventListener
      );
    }
    // Initial fetch on mount
    fetchBudgetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBudgetData = useCallback(
    async (forceRefresh = false) => {
      if (isFetchingRef.current) {
        console.log("⏭️ Fetch already in progress, skipping...");
        return;
      }

      // Skip if data is fresh and not forcing refresh
      if (!forceRefresh) {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTimestampRef.current;
        if (
          timeSinceLastFetch < STALE_TIME &&
          lastFetchTimestampRef.current > 0
        ) {
          console.log("✅ Data is fresh, skipping fetch", {
            timeSinceLastFetch: Math.round(timeSinceLastFetch / 1000) + "s",
          });
          return;
        }
      }

      isFetchingRef.current = true;
      try {
        const isInitialLoad = !hasLoadedOnceRef.current;
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setIsRefreshing(true);
        }
        setDebtsLoading(true);
        // Reset transient UI states for this fetch attempt.
        if (isInitialLoad) {
          setInitialLoadFailed(false);
          setInitialLoadError("");
        }
        setRefreshLoadWarning("");

        const customerId = await resolveCustomerId();
        if (!customerId) {
          throw new Error("Customer ID not found");
        }
        resolvedCustomerIdRef.current = customerId;

        console.log("📥 Fetching budget data for customer ID:", customerId);

        const userId = user?.userId;
        if (!userId) {
          console.warn(
            "User ID not found; skipping profile fetch for /customers/{userId}"
          );
        }

        // Prepare profile fetch using userId (the /customers/{id} endpoint expects userId, not customerId)
        const profilePromise = userId
          ? ProfileService.getCustomerData(userId, {
              skipErrorCapture: true,
              skipCache: true,
            }).catch(() => ({ success: false, data: null }))
          : Promise.resolve({ success: false, data: null });

        type LabeledResult<T> =
          | { ok: true; label: string; value: T }
          | { ok: false; label: string; error: unknown };

        const runFetchOnce = async (): Promise<{
          results: readonly LabeledResult<unknown>[];
          failedLabels: string[];
        }> => {
          const labels = [
            "income",
            "incomeNotes",
            "assets",
            "assetNotes",
            "expenses",
            "expenseNotes",
            "priorityDebts",
            "nonPriorityDebts",
            "accountTypes",
            "profile",
            "presentedAnswered",
            "calculatedTotals",
            "triggers",
          ] as const;

          const settled = await Promise.allSettled([
            BudgetService.getIncomeData(customerId, { skipCache: true }),
            BudgetService.getIncomeNotes(customerId),
            BudgetService.getAssetData(customerId, { skipCache: true }),
            BudgetService.getAssetNotes(customerId),
            BudgetService.getExpenseData(customerId, { skipCache: true }),
            BudgetService.getExpenseNotes(customerId),
            DebtsService.getPriorityDebts(customerId),
            DebtsService.getNonPriorityDebts(customerId),
            DebtCompaniesService.fetchAllAccountTypes({
              skipErrorCapture: true,
            }),
            profilePromise,
            BudgetService.getCustomerPresentedAnswered(customerId),
            httpGet<ApiResponse>(
              customerEndpoints.getCalculatedTotals(customerId)
            ),
            BudgetService.getIncomeTriggers({ skipErrorCapture: true }),
          ] as const);

          const results: LabeledResult<unknown>[] = settled.map((r, idx) => {
            const label = labels[idx] as string;
            return r.status === "fulfilled"
              ? { ok: true, label, value: r.value }
              : { ok: false, label, error: r.reason };
          });

          const failedLabels = results
            .filter(
              (r): r is { ok: false; label: string; error: unknown } => !r.ok
            )
            .map((r) => r.label);

          return { results, failedLabels };
        };

        const runFetchWithRetryOnce = async (): Promise<{
          results: readonly LabeledResult<unknown>[];
          failedLabels: string[];
        }> => {
          const first = await runFetchOnce();
          if (first.failedLabels.length === 0) return first;
          // Retry once after a short delay (transient network hiccup)
          await sleep(600);
          return await runFetchOnce();
        };

        const { results, failedLabels } = await runFetchWithRetryOnce();

        const getByLabel = <T,>(label: string): T | null => {
          const hit = results.find((r) => r.label === label);
          return hit && hit.ok ? (hit.value as T) : null;
        };

        const essentialFailures = failedLabels.filter((k) =>
          [
            "income",
            "incomeNotes",
            "assets",
            "assetNotes",
            "expenses",
            "expenseNotes",
            "priorityDebts",
            "nonPriorityDebts",
            "calculatedTotals",
          ].includes(k)
        );

        // Initial-only blocking behaviour (Option B): if essential budget data couldn't be fetched,
        // show a retry screen instead of rendering £0.00 placeholders.
        if (isInitialLoad && essentialFailures.length > 0) {
          setInitialLoadFailed(true);
          setInitialLoadError(
            `Failed to load budget data (${essentialFailures.join(", ")}).`
          );
          setLoading(false);
          setIsRefreshing(false);
          setDebtsLoading(false);
          return;
        }

        // If this is a refresh and anything failed, keep last-known-good data and surface a warning.
        // Do NOT wipe existing records to null/empty; only update what we successfully fetched.
        if (!isInitialLoad && failedLabels.length > 0) {
          setRefreshLoadWarning(
            `Some budget data couldn’t be refreshed (${failedLabels.join(", ")}). Showing the last saved values.`
          );
          if (shouldShowRefreshWarning()) {
            showAlert({
              severity: "warning",
              message:
                "Some budget data couldn’t be refreshed. Showing the last saved values.",
              autoHide: true,
              duration: 4000,
            });
          }
        }

        const incomeResp = getByLabel<any>("income");
        const incomeNotesResp = getByLabel<any>("incomeNotes");
        const assetResp = getByLabel<any>("assets");
        const assetNotesResp = getByLabel<any>("assetNotes");
        const expenseResp = getByLabel<any>("expenses");
        const expenseNotesResp = getByLabel<any>("expenseNotes");
        const priorityDebtsResp = getByLabel<any>("priorityDebts");
        const nonPriorityDebtsResp = getByLabel<any>("nonPriorityDebts");
        const accountTypesResp = getByLabel<any>("accountTypes");
        const profileResp = getByLabel<any>("profile");
        const presentedResp = getByLabel<any>("presentedAnswered");
        const calculatedTotalsResp = getByLabel<any>("calculatedTotals");
        const triggersResp = getByLabel<any>("triggers");

        // Set income data
        if (
          incomeResp?.success &&
          Array.isArray(incomeResp.data) &&
          incomeResp.data.length > 0
        ) {
          console.log("Income data received:", incomeResp.data[0]);
          setIncomeRecord(incomeResp.data[0]);
          lastIncomeSnapshotRef.current = incomeResp.data[0];
        } else {
          // On refresh failure, do not clobber existing data.
          if (isInitialLoad) {
            console.log("No income data received");
            setIncomeRecord(null);
          }
        }
        if (
          incomeNotesResp?.success &&
          Array.isArray(incomeNotesResp.data) &&
          incomeNotesResp.data.length > 0
        ) {
          console.log("Income notes received:", incomeNotesResp.data[0]);
          setIncomeNotesRecord((incomeNotesResp.data[0] as any) || {});
          lastIncomeNotesSnapshotRef.current =
            (incomeNotesResp.data[0] as any) || {};
        } else {
          if (isInitialLoad) {
            setIncomeNotesRecord({});
          }
        }

        // Set asset data
        if (
          assetResp?.success &&
          Array.isArray(assetResp.data) &&
          assetResp.data.length > 0
        ) {
          console.log("Asset data received:", assetResp.data[0]);
          setAssetRecord(assetResp.data[0]);
        } else {
          if (isInitialLoad) {
            console.log("No asset data received");
            setAssetRecord(null);
          }
        }
        if (
          assetNotesResp?.success &&
          Array.isArray(assetNotesResp.data) &&
          assetNotesResp.data.length > 0
        ) {
          console.log("Asset notes received:", assetNotesResp.data[0]);
          setAssetNotesRecord((assetNotesResp.data[0] as any) || {});
        } else {
          if (isInitialLoad) {
            setAssetNotesRecord({});
          }
        }

        // Set expense data
        if (
          expenseResp?.success &&
          Array.isArray(expenseResp.data) &&
          expenseResp.data.length > 0
        ) {
          console.log("Expense data received:", expenseResp.data[0]);
          setExpenseRecord(expenseResp.data[0]);
          lastExpenseSnapshotRef.current = expenseResp.data[0];
        } else {
          if (isInitialLoad) {
            console.log("No expense data received");
            setExpenseRecord(null);
          }
        }
        if (
          expenseNotesResp?.success &&
          Array.isArray(expenseNotesResp.data) &&
          expenseNotesResp.data.length > 0
        ) {
          console.log("Expense notes received:", expenseNotesResp.data[0]);
          setExpenseNotesRecord((expenseNotesResp.data[0] as any) || {});
          lastExpenseNotesSnapshotRef.current =
            (expenseNotesResp.data[0] as any) || {};
        } else {
          if (isInitialLoad) {
            setExpenseNotesRecord({});
          }
        }

        // Triggers drive "Quick check" limit prompts in the warning engine
        if (
          triggersResp?.success &&
          Array.isArray((triggersResp as any).data)
        ) {
          setIncomeTriggers(
            (triggersResp as any).data as IncomeTriggerRecord[]
          );
        } else {
          if (isInitialLoad) {
            setIncomeTriggers([]);
          }
        }

        // Resolve debt account types used for display labels (shared for priority & non-priority, including arrears)
        const accountTypesForMapping: AccountTypeRecord[] =
          accountTypesResp?.success && Array.isArray(accountTypesResp.data)
            ? accountTypesResp.data
            : [];

        const enhanceDebtTypeDisplayNames = (
          debts: DebtItem[],
          accountTypes: AccountTypeRecord[]
        ): DebtItem[] => {
          const lc = (value: string): string => value.trim().toLowerCase();

          return debts.map((debt) => {
            const rawCode = (debt.debtType || "").trim();
            if (!rawCode || rawCode === "-") {
              return debt;
            }

            const target = lc(rawCode);

            const hit = accountTypes.find((opt) => {
              const codeStr = lc(String(opt.code ?? ""));
              const labelStr = lc(String(opt.label ?? ""));
              return codeStr === target || labelStr === target;
            });

            const debtTypeDisplay = hit?.label || debt.debtTypeDisplay || "";

            return {
              ...debt,
              debtTypeDisplay,
            };
          });
        };

        const buildEnhancedDebts = (
          records: CustomerDebtRecord[]
        ): DebtItem[] =>
          enhanceDebtTypeDisplayNames(
            mapDebtRecordsToItems(records),
            accountTypesForMapping
          );

        const priorityRecords =
          priorityDebtsResp?.success && Array.isArray(priorityDebtsResp.data)
            ? (priorityDebtsResp.data as CustomerDebtRecord[])
            : [];
        const nonPriorityRecords =
          nonPriorityDebtsResp?.success &&
          Array.isArray(nonPriorityDebtsResp.data)
            ? (nonPriorityDebtsResp.data as CustomerDebtRecord[])
            : [];

        let priorityDebtItems = buildEnhancedDebts(priorityRecords);
        let nonPriorityDebtItems = buildEnhancedDebts(nonPriorityRecords);

        const needsFallback =
          !priorityDebtsResp?.success || !nonPriorityDebtsResp?.success;

        if (needsFallback) {
          try {
            const fallbackResp = await DebtsService.getDebts(customerId);
            if (fallbackResp?.success && Array.isArray(fallbackResp.data)) {
              const { priority, nonPriority } = splitDebtsByPriority(
                fallbackResp.data as CustomerDebtRecord[]
              );
              priorityDebtItems = buildEnhancedDebts(priority);
              nonPriorityDebtItems = buildEnhancedDebts(nonPriority);
            } else {
              priorityDebtItems = [];
              nonPriorityDebtItems = [];
            }
          } catch (fallbackError) {
            console.warn(
              "Failed to fetch combined debts for fallback",
              fallbackError
            );
            priorityDebtItems = [];
            nonPriorityDebtItems = [];
          }
        }

        setPriorityDebts(priorityDebtItems);
        setNonPriorityDebts(nonPriorityDebtItems);
        setDebtsLoading(false);

        // Set profile data
        if (profileResp?.success && profileResp.data) {
          let customer: any = null;
          const d = profileResp.data as any;
          if (Array.isArray(d) && d.length > 0) customer = d[0];
          else customer = d;
          setProfileData(customer || null);
        } else {
          if (isInitialLoad) {
            setProfileData(null);
          }
        }

        // Note: presentedResp data logged but not currently used in UI
        if (presentedResp?.success) {
          console.log("Presented/Answered summary:", presentedResp.data);
        }

        // Set calculated totals if available (server-side source of truth)
        try {
          const rawTotals =
            (calculatedTotalsResp as any)?.data?.data ??
            ((calculatedTotalsResp as any)?.data as unknown as any[]);
          const summary: CalculatedTotals | null = Array.isArray(rawTotals)
            ? (rawTotals[0] as CalculatedTotals)
            : ((rawTotals as CalculatedTotals) ?? null);
          setCalculatedTotals(summary || null);
        } catch (e) {
          if (isInitialLoad) {
            setCalculatedTotals(null);
          }
        }

        // Update last fetch timestamp only when essential calls succeeded (otherwise keep data stale so we retry sooner).
        if (essentialFailures.length === 0) {
          lastFetchTimestampRef.current = Date.now();
        }
        console.log(
          "✅ Budget data fetched successfully at",
          new Date().toLocaleTimeString()
        );

        hasLoadedOnceRef.current = true;
        setLoading(false);
        setIsRefreshing(false);
        setDebtsLoading(false);
      } catch (error) {
        console.error("❌ Error fetching budget data:", error);
        // Initial-only blocking behaviour: if the very first load fails at a high level
        // (e.g., resolveCustomerId or unexpected throw), show the retry screen.
        if (!hasLoadedOnceRef.current) {
          setInitialLoadFailed(true);
          setInitialLoadError(
            error instanceof Error
              ? error.message
              : "Failed to load budget data."
          );
        } else {
          setRefreshLoadWarning(
            "Some budget data couldn’t be refreshed. Showing the last saved values."
          );
          if (shouldShowRefreshWarning()) {
            showAlert({
              severity: "warning",
              message:
                "Some budget data couldn’t be refreshed. Showing the last saved values.",
              autoHide: true,
              duration: 4000,
            });
          }
        }
        setLoading(false);
        setIsRefreshing(false);
        setDebtsLoading(false);
      } finally {
        isFetchingRef.current = false;
      }
    },
    [
      resolveCustomerId,
      user?.userId,
      sleep,
      shouldShowRefreshWarning,
      showAlert,
    ]
  );

  // ---- Warning dialog handlers (match updateBudgetPage behaviour) ---------------

  const resolveUserIdSafe = useCallback((): number | null => {
    if (typeof user?.userId === "number") return user.userId;
    try {
      const raw =
        typeof window !== "undefined" ? localStorage.getItem("userData") : null;
      const parsed = raw ? (JSON.parse(raw) as any) : null;
      const id = parsed?.userId;
      return typeof id === "number" ? id : null;
    } catch {
      return null;
    }
  }, [user?.userId]);

  const fetchBudgetSnapshotsFresh = useCallback(
    async (customerId: number): Promise<BudgetSnapshots> => {
      const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
        await Promise.all([
          BudgetService.getIncomeData(customerId, { skipCache: true }).catch(
            () => ({ success: false, data: [] }) as any
          ),
          BudgetService.getIncomeNotes(customerId).catch(
            () => ({ success: false, data: [] }) as any
          ),
          BudgetService.getExpenseData(customerId, { skipCache: true }).catch(
            () => ({ success: false, data: [] }) as any
          ),
          BudgetService.getExpenseNotes(customerId).catch(
            () => ({ success: false, data: [] }) as any
          ),
        ]);

      const income =
        incomeResp?.success &&
        Array.isArray(incomeResp.data) &&
        incomeResp.data.length > 0
          ? (incomeResp.data[0] as any)
          : null;
      const incomeNotes =
        incomeNotesResp?.success &&
        Array.isArray(incomeNotesResp.data) &&
        incomeNotesResp.data.length > 0
          ? (incomeNotesResp.data[0] as any)
          : null;
      const expenses =
        expenseResp?.success &&
        Array.isArray(expenseResp.data) &&
        expenseResp.data.length > 0
          ? (expenseResp.data[0] as any)
          : null;
      const expenseNotes =
        expenseNotesResp?.success &&
        Array.isArray(expenseNotesResp.data) &&
        expenseNotesResp.data.length > 0
          ? (expenseNotesResp.data[0] as any)
          : null;

      return {
        income,
        incomeNotes,
        expenses,
        expenseNotes,
      } as BudgetSnapshots;
    },
    []
  );

  const clearBudgetForPlan = useCallback(
    async (
      plan: ReturnType<typeof computeConfirmBeforeClearPlan>
    ): Promise<void> => {
      try {
        if (plan.clearPayloads.incomeData) {
          await BudgetService.updateIncomeData(plan.clearPayloads.incomeData);
        }
        if (plan.clearPayloads.incomeNotes) {
          await BudgetService.updateIncomeNotes(plan.clearPayloads.incomeNotes);
        }
        if (plan.clearPayloads.expenseData) {
          await BudgetService.updateExpenseData(plan.clearPayloads.expenseData);
        }
        if (plan.clearPayloads.expenseNotes) {
          await BudgetService.updateExpenseNotes(
            plan.clearPayloads.expenseNotes
          );
        }
      } catch {
        // Non-blocking: if clearing fails, proceed with profile update
      }
    },
    []
  );

  const getInitialDataForProfileDialog = useCallback(
    (stepIndex: number): Record<string, unknown> => {
      const initial: Record<string, unknown> = {};

      const stored =
        typeof window !== "undefined"
          ? (() => {
              try {
                const raw = localStorage.getItem("customerData");
                return raw ? (JSON.parse(raw) as any) : null;
              } catch {
                return null;
              }
            })()
          : null;

      const base =
        profileData && typeof profileData === "object"
          ? (profileData as any)
          : stored && typeof stored === "object"
            ? stored
            : {};

      const parseDobToDate = (value: unknown): Date | null => {
        if (!value) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime()))
          return value;
        if (typeof value === "string") {
          const d = new Date(value);
          if (!Number.isNaN(d.getTime())) return d;
        }
        return null;
      };

      switch (stepIndex) {
        case 0: {
          const accomodation =
            (base as any).housingTenure ??
            (base as any).accommodationType ??
            (base as any).accomodationType;
          if (typeof accomodation === "string" && accomodation.trim()) {
            initial.accomodationType = accomodation;
          }
          break;
        }
        case 1: {
          const status = (base as any).customerEmploymentStatus;
          if (typeof status === "string" && status.trim()) {
            initial.customerEmploymentStatus = status;
          }
          break;
        }
        case 2: {
          const joint = String((base as any).jointAccount ?? "")
            .trim()
            .toUpperCase();
          const withPartner = joint === "Y";
          initial.budgetWithPartner = withPartner;
          if (withPartner) {
            const name = (base as any).debtor2Name;
            if (typeof name === "string" && name.trim()) {
              initial.name = name;
            }
            const dob =
              (base as any).debtor2DateOfBirthStr ??
              (base as any).debtor2DateOfBirth ??
              (base as any).partnerDateOfBirth;
            const parsedDob = parseDobToDate(dob);
            if (parsedDob) initial.dob = parsedDob;
            const emp = (base as any).partnerEmploymentStatus;
            if (typeof emp === "string" && emp.trim()) {
              initial.employmentStatus = emp;
            }
          }
          break;
        }
        case 3: {
          const u16 = Number(
            (base as any).dependentChildrenUnder16 ??
              (base as any).dependantsUnder16 ??
              0
          );
          const a16 = Number(
            (base as any).dependentChildrenAged16Plus ??
              (base as any).dependentChildrenaged16plus ??
              0
          );
          // adultsOver18IncludingMyself is actually other adults excluding customer and partner
          const adults = (base as any).adultsOver18IncludingMyself;
          const adultsNum =
            typeof adults === "number" ? adults : Number(adults);
          const otherAdultsOver18 = Number.isFinite(adultsNum) && adultsNum >= 0
            ? adultsNum
            : 0;
          
          // Only pre-fill if the user has explicitly answered this question
          const haveChildrenRaw = (base as any).haveChildrenUnder18;
          const hasExistingAnswer = haveChildrenRaw !== undefined && haveChildrenRaw !== null;
          
          if (hasExistingAnswer) {
            // haveDependents is true if there are children under 18 OR other adults
            const hasChildren = String(haveChildrenRaw ?? "")
              .trim()
              .toUpperCase() === "Y" || u16 + a16 > 0;
            const have = hasChildren || otherAdultsOver18 > 0;
            initial.haveDependents = have;
            if (have) {
              if (Number.isFinite(u16) && u16 >= 0)
                initial.under16Dependents = u16;
              if (Number.isFinite(a16) && a16 >= 0)
                initial.between16to18Dependents = a16;
              initial.otherAdultsOver18 = otherAdultsOver18;
            }
          }
          break;
        }
        case 4: {
          const vehiclesRaw =
            (base as any).noOfVehiclesInHouseholds ??
            (base as any).vehicles ??
            (base as any).vehiclesCount;
          const vehicleCount =
            typeof vehiclesRaw === "number"
              ? vehiclesRaw
              : Number(vehiclesRaw || 0);
          const haveVehicle = vehicleCount > 0;
          initial.haveVehicle = haveVehicle;
          if (haveVehicle) {
            initial.noOfVehiclesInHouseholds = Number.isFinite(vehicleCount)
              ? vehicleCount
              : 1;
          }
          break;
        }
        case 5: {
          const status = (base as any).partnerEmploymentStatus;
          if (typeof status === "string" && status.trim()) {
            initial.partnerEmploymentStatus = status;
          }
          break;
        }
        default:
          break;
      }

      return initial;
    },
    [profileData]
  );

  const openProfileWarningDialog = useCallback(
    (key: ProfileDialogKey): boolean => {
      const stepIndex = PROFILE_DIALOG_STEP_MAP[key];
      if (typeof stepIndex !== "number") return false;

      const schema =
        stepIndex === 5
          ? (partnerEmploymentSchema as unknown)
          : (incomeOnboardingSchemas[stepIndex] as unknown);
      if (!schema) return false;

      setProfileDialogState({
        key,
        stepIndex,
        schema,
        title: PROFILE_DIALOG_TITLES[key] ?? "Update details",
        initialData: getInitialDataForProfileDialog(stepIndex),
      });
      return true;
    },
    [getInitialDataForProfileDialog]
  );

  const handleWarningDialog = useCallback(
    (action: OpenActionSpec, _metadata?: { varId?: string }): boolean => {
      if (!action) return false;
      if (action.type !== "dialog") return false;
      const innerKey = typeof action.inner === "string" ? action.inner : "";

      if (
        action.outer === "profile" &&
        innerKey &&
        innerKey in PROFILE_DIALOG_STEP_MAP
      ) {
        // Avoid stacking dialogs: close the inline edit dialog before opening profile dialog
        setIsEditFieldDialogOpen(false);
        setEditDialogConfig(null);
        setIsDialogLoading(false);
        return openProfileWarningDialog(innerKey as ProfileDialogKey);
      }

      return false;
    },
    [openProfileWarningDialog]
  );

  const handleProfileDialogSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      if (!profileDialogState) return;

      // Prefer the already-loaded budget records as the source of truth for customerId.
      // On this page, localStorage.customerData can sometimes contain a profile record where `id`
      // is actually a userId (not a customerId), which can cause resolveCustomerId() to return
      // the wrong identifier and make confirm-before-clear silently skip.
      const customerIdFromBudget =
        typeof (incomeRecord as any)?.customerId === "number"
          ? ((incomeRecord as any).customerId as number)
          : typeof (expenseRecord as any)?.customerId === "number"
            ? ((expenseRecord as any).customerId as number)
            : null;

      const customerIdRaw =
        customerIdFromBudget ??
        resolvedCustomerIdRef.current ??
        (await resolveCustomerId());
      const customerId =
        typeof customerIdRaw === "number"
          ? customerIdRaw
          : typeof customerIdRaw === "string"
            ? parseInt(customerIdRaw, 10)
            : null;
      const userId = resolveUserIdSafe();

      if (!customerId || !userId) {
        showAlert({
          severity: "error",
          message: "Missing customer or user",
          autoHide: true,
          duration: 2500,
        });
        return;
      }

      setIsProfileDialogSaving(true);

      const baseRecord = (() => {
        if (profileData && typeof profileData === "object")
          return profileData as any;
        try {
          const raw =
            typeof window !== "undefined"
              ? localStorage.getItem("customerData")
              : null;
          const parsed = raw ? (JSON.parse(raw) as any) : null;
          return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
          return null;
        }
      })();

      if (!baseRecord) {
        showAlert({
          severity: "error",
          message: "Couldn’t update your profile right now.",
          autoHide: true,
          duration: 2500,
        });
        setIsProfileDialogSaving(false);
        return;
      }

      const payload: Record<string, any> = {};
      const afterOverrides: Partial<ProfileBudgetContext> = {};

      try {
        const stepIndex = profileDialogState.stepIndex;

        switch (stepIndex) {
          case 0: {
            const accommodation =
              typeof (formData as any).accomodationType === "string"
                ? String((formData as any).accomodationType)
                : "";
            if (accommodation.trim()) {
              payload.housingTenure = accommodation;
              afterOverrides.accommodationType = accommodation;
            }
            break;
          }
          case 1: {
            const employment =
              typeof (formData as any).customerEmploymentStatus === "string"
                ? String((formData as any).customerEmploymentStatus)
                : "";
            if (employment.trim()) {
              payload.customerEmploymentStatus = employment;
              afterOverrides.customerEmploymentStatus = employment;
            }
            break;
          }
          case 2: {
            if ((formData as any).budgetWithPartner !== undefined) {
              const withPartner = (formData as any).budgetWithPartner === true;
              payload.jointAccount = withPartner ? "Y" : "N";
              afterOverrides.jointAccount = payload.jointAccount;

              if (withPartner) {
                const partnerName =
                  typeof (formData as any).name === "string"
                    ? String((formData as any).name)
                    : "";
                if (partnerName.trim()) {
                  payload.debtor2Name = partnerName;
                }

                const dobValue = (formData as any).dob;
                let dobString: string | null = null;
                if (
                  dobValue instanceof Date &&
                  !Number.isNaN(dobValue.getTime())
                ) {
                  const year = dobValue.getFullYear();
                  const month = String(dobValue.getMonth() + 1).padStart(
                    2,
                    "0"
                  );
                  const day = String(dobValue.getDate()).padStart(2, "0");
                  dobString = `${year}-${month}-${day}`;
                } else if (typeof dobValue === "string" && dobValue) {
                  const parsed = new Date(dobValue);
                  if (!Number.isNaN(parsed.getTime())) {
                    const year = parsed.getFullYear();
                    const month = String(parsed.getMonth() + 1).padStart(
                      2,
                      "0"
                    );
                    const day = String(parsed.getDate()).padStart(2, "0");
                    dobString = `${year}-${month}-${day}`;
                  }
                }
                if (dobString) {
                  payload.debtor2DateOfBirthStr = dobString;
                  payload.debtor2DateOfBirth = dobString;
                }

                const partnerEmployment =
                  typeof (formData as any).employmentStatus === "string"
                    ? String((formData as any).employmentStatus)
                    : "";
                if (partnerEmployment.trim()) {
                  payload.partnerEmploymentStatus = partnerEmployment;
                  afterOverrides.partnerEmploymentStatus = partnerEmployment;
                }
              } else {
                payload.debtor2Name = "";
                payload.debtor2DateOfBirthStr = "";
                payload.debtor2DateOfBirth = "";
                payload.partnerEmploymentStatus = "";
                afterOverrides.partnerEmploymentStatus = "";
              }
            }
            break;
          }
          case 3: {
            if ((formData as any).haveDependents !== undefined) {
              const hasDependents = (formData as any).haveDependents === true;
              payload.haveChildrenUnder18 = hasDependents ? "Y" : "N";
              afterOverrides.haveChildrenUnder18 = payload.haveChildrenUnder18;
              if (!hasDependents) {
                payload.dependentChildrenUnder16 = 0;
                payload.dependentChildrenAged16Plus = 0;
                afterOverrides.dependentChildrenUnder16 = 0;
                afterOverrides.dependentChildrenAged16Plus = 0;
              }
            }
            const under16Raw = (formData as any).under16Dependents;
            const between16Raw = (formData as any).between16to18Dependents;
            const u16 =
              typeof under16Raw === "number"
                ? under16Raw
                : typeof under16Raw === "string"
                  ? parseInt(under16Raw, 10)
                  : NaN;
            const a16 =
              typeof between16Raw === "number"
                ? between16Raw
                : typeof between16Raw === "string"
                  ? parseInt(between16Raw, 10)
                  : NaN;
            if (Number.isFinite(u16)) {
              payload.dependentChildrenUnder16 = u16;
              afterOverrides.dependentChildrenUnder16 = u16;
            }
            if (Number.isFinite(a16)) {
              payload.dependentChildrenAged16Plus = a16;
              afterOverrides.dependentChildrenAged16Plus = a16;
            }
            const adultsRaw = (formData as any).otherAdultsOver18;
            const adults =
              typeof adultsRaw === "number"
                ? adultsRaw
                : typeof adultsRaw === "string"
                  ? parseInt(adultsRaw, 10)
                  : NaN;
            if (Number.isFinite(adults)) {
              // adultsOver18IncludingMyself is actually other adults excluding customer and partner
              payload.adultsOver18IncludingMyself = adults;
            }
            break;
          }
          case 4: {
            if ((formData as any).haveVehicle !== undefined) {
              const hasVehicle = (formData as any).haveVehicle === true;
              const countRaw = (formData as any).noOfVehiclesInHouseholds;
              const count =
                typeof countRaw === "number"
                  ? countRaw
                  : typeof countRaw === "string"
                    ? parseInt(countRaw, 10)
                    : NaN;
              payload.noOfVehiclesInHouseholds = hasVehicle
                ? Number.isFinite(count) && count > 0
                  ? count
                  : 1
                : 0;
            }
            break;
          }
          case 5: {
            const employment =
              typeof (formData as any).partnerEmploymentStatus === "string"
                ? String((formData as any).partnerEmploymentStatus)
                : "";
            if (employment.trim()) {
              payload.partnerEmploymentStatus = employment;
              afterOverrides.partnerEmploymentStatus = employment;
            }
            break;
          }
          default:
            break;
        }

        const baseCtx = buildBaseVisibilityContext();
        const before: ProfileBudgetContext = {
          customerEmploymentStatus: (baseCtx as any).customerEmploymentStatus,
          partnerEmploymentStatus: (baseCtx as any).partnerEmploymentStatus,
          jointAccount: (baseCtx as any).jointAccount,
          accommodationType: (baseCtx as any).accommodationType,
          dependentChildrenUnder16: (baseCtx as any).dependentChildrenUnder16,
          dependentChildrenAged16Plus: (baseCtx as any)
            .dependentChildrenAged16Plus,
          haveChildrenUnder18: (baseCtx as any).haveChildrenUnder18,
          customerAge: (baseCtx as any).customerAge,
          partnerAge: (baseCtx as any).partnerAge,
        };

        const after: ProfileBudgetContext = { ...before, ...afterOverrides };

        // Build snapshots using the page's already-loaded records first (what the user is
        // currently seeing), then fall back to a fresh fetch if needed.
        const fetchedSnaps = await fetchBudgetSnapshotsFresh(customerId);
        const stableIncome =
          (incomeRecord as any) ??
          lastIncomeSnapshotRef.current ??
          fetchedSnaps.income;
        const stableIncomeNotes =
          (incomeNotesRecord &&
          typeof incomeNotesRecord === "object" &&
          Object.keys(incomeNotesRecord).length > 0
            ? (incomeNotesRecord as any)
            : (lastIncomeNotesSnapshotRef.current ??
              fetchedSnaps.incomeNotes)) ?? null;
        const stableExpense =
          (expenseRecord as any) ??
          lastExpenseSnapshotRef.current ??
          fetchedSnaps.expenses;
        const stableExpenseNotes =
          (expenseNotesRecord &&
          typeof expenseNotesRecord === "object" &&
          Object.keys(expenseNotesRecord).length > 0
            ? (expenseNotesRecord as any)
            : (lastExpenseNotesSnapshotRef.current ??
              fetchedSnaps.expenseNotes)) ?? null;

        const snaps: BudgetSnapshots = {
          income: stableIncome,
          incomeNotes: stableIncomeNotes,
          expenses: stableExpense,
          expenseNotes: stableExpenseNotes,
        };
        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId,
        });

        // Safety-net (legacy parity): some environments clear salary server-side when moving to
        // Unemployed. Even if the planner fails to detect the stored value (ID mismatch / stale
        // snapshot), we must still warn when "Take home salary" currently has a value and will
        // be hidden after this change.
        const shouldForceConfirmForSalary = (() => {
          if (stepIndex !== 1) return false;
          const normalizeStatus = (v: unknown): string => {
            if (typeof v !== "string") return "";
            return v
              .trim()
              .replace(/\s+/g, " ")
              .replace(/\s*\/\s*/g, "/");
          };
          const nextStatus = normalizeStatus(
            payload.customerEmploymentStatus
          ).toLowerCase();
          if (nextStatus !== "unemployed") return false;

          const rawAmount =
            (snaps.income as any)?.basicIncome ??
            (snaps.income as any)?.basic_income;
          const amountNum =
            typeof rawAmount === "number"
              ? rawAmount
              : typeof rawAmount === "string"
                ? Number(String(rawAmount).replace(/[,£\s]/g, ""))
                : NaN;
          const hasAmount = Number.isFinite(amountNum) && amountNum > 0;

          const rawNotes =
            (snaps.incomeNotes as any)?.basicIncomeNotes ??
            (snaps.incomeNotes as any)?.basic_income_notes;
          const hasNotes =
            typeof rawNotes === "string" && rawNotes.trim().length > 0;

          return hasAmount || hasNotes;
        })();

        if (isDebugConfirmEnabled()) {
          try {
            const rawAmount =
              (snaps.income as any)?.basicIncome ??
              (snaps.income as any)?.basic_income;
            const rawNotes =
              (snaps.incomeNotes as any)?.basicIncomeNotes ??
              (snaps.incomeNotes as any)?.basic_income_notes;
            showAlert({
              severity: "info",
              message: `debugConfirm: step=${stepIndex} next=${String(
                payload.customerEmploymentStatus ?? ""
              )} plan=${String(plan.needsConfirm)} forceSalary=${String(
                shouldForceConfirmForSalary
              )} basicIncome=${String(rawAmount ?? "")} notesLen=${
                typeof rawNotes === "string" ? rawNotes.trim().length : 0
              }`,
              autoHide: true,
              duration: 6000,
            });
          } catch {
            // ignore
          }
        }

        const effectivePlan =
          !plan.needsConfirm && shouldForceConfirmForSalary
            ? (() => {
                const nowIso = new Date().toISOString();
                const forced = {
                  ...plan,
                  needsConfirm: true,
                  clearedLabels: Array.from(
                    new Set([...(plan.clearedLabels || []), "Take home salary"])
                  ),
                  clearPayloads: { ...(plan.clearPayloads || {}) },
                } as typeof plan;

                const income = snaps.income as any;
                if (income && !forced.clearPayloads.incomeData) {
                  forced.clearPayloads.incomeData = {
                    ...(income as Record<string, unknown>),
                    id: income.id,
                    customerId: income.customerId,
                    lastUpdatedBy: userId,
                    lastUpdateDate: nowIso,
                    basicIncome: 0,
                  } as any;
                }

                const incomeNotes = snaps.incomeNotes as any;
                if (
                  incomeNotes &&
                  income &&
                  !forced.clearPayloads.incomeNotes
                ) {
                  forced.clearPayloads.incomeNotes = {
                    ...(incomeNotes as Record<string, unknown>),
                    id: incomeNotes.id,
                    customerId: income.customerId,
                    lastUpdatedBy: userId,
                    lastUpdateDate: nowIso,
                    basicIncomeNotes: "",
                  } as any;
                }

                return forced;
              })()
            : plan;

        const doSave = async () => {
          const putPayload: Record<string, any> = {
            ...(baseRecord as any),
            ...(payload as any),
          };

          // Guard: backend sometimes treats { customerId: userId } as invalid; match onboarding behaviour
          if (
            typeof putPayload.customerId === "number" &&
            typeof userId === "number" &&
            putPayload.customerId === userId
          ) {
            delete putPayload.customerId;
          }

          const response = await httpPut<Record<string, any>>(
            onboardingEndpoints.updateCustomer,
            putPayload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const updatedRecord = response?.data;
          if (updatedRecord && typeof updatedRecord === "object") {
            setProfileData(updatedRecord);
            try {
              localStorage.setItem(
                "customerData",
                JSON.stringify(updatedRecord)
              );
            } catch {
              // ignore
            }
          } else {
            setProfileData((prev: any) =>
              prev ? { ...prev, ...payload } : prev
            );
            try {
              const raw = localStorage.getItem("customerData");
              const parsed = raw ? JSON.parse(raw) : null;
              if (parsed && typeof parsed === "object") {
                localStorage.setItem(
                  "customerData",
                  JSON.stringify({ ...parsed, ...payload })
                );
              }
            } catch {
              // ignore
            }
          }

          showAlert({
            severity: "success",
            message:
              PROFILE_DIALOG_SUCCESS_MESSAGES[profileDialogState.key] ??
              "Updated successfully",
            autoHide: true,
            duration: 2000,
          });

          setWarningsRefreshKey((prev) => prev + 1);
          setProfileDialogState(null);
          await fetchBudgetData(true);
        };

        if (effectivePlan.needsConfirm) {
          setConfirmClearedLabels(effectivePlan.clearedLabels || []);
          // IMPORTANT: wrap in an extra function so React stores the callback instead of
          // treating it as a state-updater and executing it immediately.
          setConfirmPendingAction(() => async () => {
            setConfirmOpen(false);
            setConfirmArmed(false);
            setIsProfileDialogSaving(true);
            try {
              await clearBudgetForPlan(effectivePlan);
              await doSave();
            } finally {
              setIsProfileDialogSaving(false);
            }
          });
          // eslint-disable-next-line no-console
          console.log("[confirmBeforeClear] opening confirm dialog", {
            stepIndex,
            nextStatus: payload.customerEmploymentStatus,
            clearedLabels: effectivePlan.clearedLabels,
          });
          // IMPORTANT: open on next tick so the user's click on "Save" cannot accidentally
          // click the newly-rendered overlay buttons (Continue/Cancel) in the same position.
          if (typeof window !== "undefined") {
            window.setTimeout(() => setConfirmOpen(true), 0);
          } else {
            setConfirmOpen(true);
          }
          setIsProfileDialogSaving(false);
          return;
        }

        await doSave();
      } catch (e) {
        console.error("Failed to save profile dialog data", e);
        showAlert({
          severity: "error",
          message: "Save failed. Please try again.",
          autoHide: true,
          duration: 3000,
        });
      } finally {
        setIsProfileDialogSaving(false);
      }
    },
    [
      profileDialogState,
      resolveUserIdSafe,
      showAlert,
      profileData,
      buildBaseVisibilityContext,
      fetchBudgetSnapshotsFresh,
      clearBudgetForPlan,
      fetchBudgetData,
      incomeRecord,
      incomeNotesRecord,
      expenseRecord,
      expenseNotesRecord,
      isDebugConfirmEnabled,
    ]
  );

  const handleConfirmCancel = useCallback(() => {
    setConfirmOpen(false);
    setConfirmArmed(false);
    setConfirmPendingAction(null);
    setConfirmClearedLabels([]);
  }, []);

  const handleConfirmContinue = useCallback(async () => {
    if (!confirmArmed) {
      return;
    }
    if (!confirmPendingAction) {
      handleConfirmCancel();
      return;
    }
    try {
      await confirmPendingAction();
    } finally {
      setConfirmPendingAction(null);
      setConfirmClearedLabels([]);
    }
  }, [confirmArmed, confirmPendingAction, handleConfirmCancel]);

  const toggleSection = (sectionId: string) => {
    const wasExpanded = expandedSections[sectionId];

    // Map section ID to card ID for highlighting
    const sectionToCardMap: Record<string, string> = {
      income: "income",
      assets: "assets",
      fixedExpense: "fixedExpenses",
      flexibleExpense: "flexibleExpenses",
      debts: "debts",
    };

    const cardId = sectionToCardMap[sectionId];

    if (wasExpanded) {
      // Collapsing - remove active state
      setActiveCardId(null);
      setExpandedSections((prev) => ({
        ...prev,
        [sectionId]: false,
      }));
    } else {
      // Expanding - set active card and collapse others
      if (cardId) {
        setActiveCardId(cardId);
      }
      setExpandedSections({
        income: false,
        assets: false,
        fixedExpense: false,
        flexibleExpense: false,
        debts: false,
        [sectionId]: true,
      });
    }
  };

  const handleSummaryCardClick = (cardId: string) => {
    // Map card IDs to section IDs
    const sectionMap: Record<string, string> = {
      income: "income",
      assets: "assets",
      fixedExpenses: "fixedExpense",
      flexibleExpenses: "flexibleExpense",
      debts: "debts",
    };

    const sectionId = sectionMap[cardId];
    if (!sectionId) {
      console.log("❌ Section not found for cardId:", cardId);
      return;
    }

    console.log("✅ Card clicked:", cardId, "→ Section ID:", sectionId);

    // Set active card
    setActiveCardId(cardId);

    // Check if section was already expanded
    const wasExpanded = expandedSections[sectionId];
    console.log("📊 Was expanded:", wasExpanded);

    // Collapse all sections first, then expand only the clicked one
    setExpandedSections({
      income: false,
      assets: false,
      fixedExpense: false,
      flexibleExpense: false,
      debts: false,
      [sectionId]: true,
    });

    // Update URL with query parameter
    navigate(`/budget?section=${sectionId}`, { replace: true });

    // Scroll to the section with appropriate delay to allow expansion
    const scrollDelay = wasExpanded ? 100 : 500;
    console.log("⏱️ Scroll delay:", scrollDelay, "ms");

    scheduleScrollToSection(sectionId, scrollDelay);
  };

  const handleEdit = (sectionId: string) => {
    // Map section IDs to outer/inner route parameters for updateBudgetPage
    let outerParam = "";
    let innerParam = "";

    switch (sectionId) {
      case "income":
        outerParam = "income";
        innerParam = "salary";
        break;
      case "assets":
        outerParam = "assets";
        innerParam = "properties";
        break;
      case "fixedExpense":
        outerParam = "fixed-expenses";
        innerParam = "household";
        break;
      case "flexibleExpense":
        outerParam = "flexible-expenses";
        innerParam = "leisure";
        break;
      case "debts":
        outerParam = "debts";
        innerParam = "debts-administration";
        break;
      default:
        outerParam = "income";
        innerParam = "salary";
    }
    BudgetService.clearIncomeDataCache();
    BudgetService.clearExpenseDataCache();
    navigate(`/update-budget?outer=${outerParam}&inner=${innerParam}`);
  };

  const handleEditItem = async (sectionId: string, itemLabel: string) => {
    const labelLower = (itemLabel || "").toLowerCase();

    // Save current scroll position before opening dialog
    const currentScrollY =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      window.scrollY ||
      0;
    setSavedScrollPosition(currentScrollY);

    // Show loading while preparing dialog
    setIsDialogLoading(true);

    // Map all income fields to their inline dialog config

    // Salary group fields
    if (labelLower === "salary") {
      setEditDialogConfig({
        groupKey: "salary",
        fieldKey: "takeHomeSalary",
        title: "Edit salary",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "partner's salary") {
      setEditDialogConfig({
        groupKey: "salary",
        fieldKey: "partnerTakeHomeSalary",
        title: "Edit partner's salary",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "other salary source") {
      setEditDialogConfig({
        groupKey: "salary",
        fieldKey: "otherSalarySource",
        title: "Edit other salary source",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }

    // Benefits group fields
    if (labelLower === "universal credit") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "universalCredit",
        title: "Edit universal credit",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "jobseeker's allowance (contribution-based)") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "jobSeekerAllowanceContribute",
        title: "Edit jobseeker's allowance (contribution-based)",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "child benefit") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "childBenefit",
        title: "Edit child benefit",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "esa or ssp") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "empsupportallowancestatutory",
        title: "Edit ESA or SSP",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "disability benefits (dla/pip/aa)") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "disabilityAllowance",
        title: "Edit disability benefits",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "carer's allowance") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "carerAllowance",
        title: "Edit carer's allowance",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "housing benefit local allowance") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "housingBenefitLocalAllowance",
        title: "Edit housing benefit",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "council tax support") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "councilTaxSupport",
        title: "Edit council tax support",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "student loans & grants") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "studentLoansAndGrants",
        title: "Edit student loans & grants",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "other benefits") {
      setEditDialogConfig({
        groupKey: "benefits",
        fieldKey: "otherBenefits",
        title: "Edit other benefits",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }

    // Pension group fields
    if (labelLower === "state pension") {
      setEditDialogConfig({
        groupKey: "pension",
        fieldKey: "statePension",
        title: "Edit state pension",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "private pension") {
      setEditDialogConfig({
        groupKey: "pension",
        fieldKey: "privatePension",
        title: "Edit private pension",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "pension credit") {
      setEditDialogConfig({
        groupKey: "pension",
        fieldKey: "pensionCredit",
        title: "Edit pension credit",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "pension others") {
      setEditDialogConfig({
        groupKey: "pension",
        fieldKey: "pensionOthers",
        title: "Edit pension others",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }

    // Other sources group fields
    if (labelLower === "child maintenance or support") {
      setEditDialogConfig({
        groupKey: "otherSources",
        fieldKey: "childMaintenanceOrSupport",
        title: "Edit child maintenance",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "borders or lodgers") {
      setEditDialogConfig({
        groupKey: "otherSources",
        fieldKey: "bordersAndLodger",
        title: "Edit borders or lodgers",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "non-dependant contributions") {
      setEditDialogConfig({
        groupKey: "otherSources",
        fieldKey: "nonDependantContributions",
        title: "Edit non-dependant contributions",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }
    if (labelLower === "money from other sources") {
      setEditDialogConfig({
        groupKey: "otherSources",
        fieldKey: "moneyFromOtherSources",
        title: "Edit money from other sources",
      });
      safeOpenEditDialog();
      setIsDialogLoading(false);
      return;
    }

    // Asset fields handling
    if (sectionId === "assets") {
      // Properties group fields
      if (labelLower === "total value of all your properties") {
        setEditDialogConfig({
          groupKey: "properties" as any,
          fieldKey: "propertiesTotalValue",
          title: "Edit property total value",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }
      if (labelLower === "outstanding on all mortgages") {
        setEditDialogConfig({
          groupKey: "properties" as any,
          fieldKey: "propertiesMortgageOutstanding",
          title: "Edit outstanding mortgages",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }
      if (labelLower === "loans secured against your property") {
        setEditDialogConfig({
          groupKey: "properties" as any,
          fieldKey: "propertiesSecuredLoanOutstanding",
          title: "Edit secured loans",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }

      // Other assets group fields
      if (labelLower === "vehicle(s) worth") {
        setEditDialogConfig({
          groupKey: "otherAssets" as any,
          fieldKey: "valueOfVehicles",
          title: "Edit vehicle value",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }
      if (labelLower === "money in savings") {
        setEditDialogConfig({
          groupKey: "otherAssets" as any,
          fieldKey: "assetSavings",
          title: "Edit savings",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }
      if (labelLower === "other assets worth") {
        setEditDialogConfig({
          groupKey: "otherAssets" as any,
          fieldKey: "otherAssets",
          title: "Edit other assets",
        });
        safeOpenEditDialog();
        setIsDialogLoading(false);
        return;
      }
    }

    // Fallback - if we didn't match any field, go to the grouped editor
    let outerParam = sectionId;
    let innerParam = "salary";

    if (sectionId === "income") {
      if (
        labelLower.includes("benefit") ||
        labelLower.includes("universal credit")
      ) {
        innerParam = "benefits";
      } else if (labelLower.includes("pension")) {
        innerParam = "pension";
      } else if (labelLower.includes("other")) {
        innerParam = "other-sources";
      }
    } else if (sectionId === "assets") {
      innerParam = "properties";
    }

    setIsDialogLoading(false);
    navigate(`/budget/update?outer=${outerParam}&inner=${innerParam}`);
  };

  const handleReviewBudgetClick = () => {
    setIsReviewBudgetDialogOpen(true);
    // Add showSummary parameter to URL for shareable link
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("showSummary", "true");
    window.history.replaceState({}, "", newUrl.toString());
  };

  const renderSmartReviewDialog = () => {
    return (
      <SmartReviewDialog
        setTriggersCount={setTriggersCount}
        open={smartReviewOpen}
        incomeItems={incomeItems}
        priorityDebts={priorityDebts}
        nonPriorityDebts={nonPriorityDebts}
        flexibleExpenseList={flexibleExpenseList}
        fixedExpenseList={fixedExpenseList}
        assetItems={assetItems}
        handleEditItem={handleEditItem}
        onClose={() => {
          setSmartReviewOpen(false);
          fetchBudgetData(true);
        }}
        setEditDialogConfig={setEditDialogConfig}
        safeOpenEditDialog={safeOpenEditDialog}
      />
    );
  };

  // Components moved to @BudgetPageCom

  // Include NI detection loading in the overall loading state
  if (loading || niLoading) {
    return (
      <DashboardLayout backgroundColor="#FFFFFF">
        <Box sx={{ pb: 6, maxWidth: "1400px", mx: "auto", width: "100%" }}>
          {/* Header skeleton */}
          <Skeleton variant="text" width="40%" height={48} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 4 }} />

          {/* Summary cards skeleton */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={12} sm={6} md={2.4} key={`summary-skel-${i}`}>
                <Skeleton
                  variant="rectangular"
                  height={140}
                  sx={{ borderRadius: "12px" }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Section skeletons (Income, Assets, Fixed/Flexible Expenses, Debts) */}
          {[
            "Income",
            "Assets",
            "Fixed expenses",
            "Flexible expenses",
            "Debts",
          ].map((label) => (
            <Box key={label} sx={{ mb: 4 }}>
              <Divider sx={{ mb: 3 }} />
              {/* Section header skeleton */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Skeleton
                  variant="circular"
                  width={28}
                  height={28}
                  sx={{ mr: 1.5 }}
                />
                <Skeleton variant="text" width="20%" height={28} />
              </Box>
              <Skeleton variant="text" width="45%" height={20} sx={{ mb: 2 }} />
              {/* Section items skeleton */}
              <Grid container spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={`${label}-skel-${i}`}>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #E2E8F0",
                        p: 2,
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="35%" height={20} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </DashboardLayout>
    );
  }

  if (initialLoadFailed) {
    return (
      <DashboardLayout backgroundColor="#FFFFFF">
        <Box sx={{ pb: 6, maxWidth: "1400px", mx: "auto", width: "100%" }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  variant="contained"
                  onClick={() => {
                    void fetchBudgetData(true);
                  }}
                >
                  Retry
                </Button>
              }
            >
              {initialLoadError ||
                "Failed to load budget data. Please try again."}
            </Alert>

            <Button
              variant="outlined"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh page
            </Button>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  // if (triggersCount == -1) {
  //   return <SimpleLoadingScreen title="Loading your budget breakdown..." />;
  // }

  return (
    <CustomerMetadataContext.Provider value={customerMetadataForWarnings}>
      <WarningsRefreshContext.Provider value={warningsRefreshKey}>
        <TriggersProvider value={triggersContext}>
          <>
            {triggersCount == -1 &&
            window.location.href.includes("showSummary") ? (
              <SimpleLoadingScreen title="Loading your budget breakdown..." />
            ) : (
              <DashboardLayout backgroundColor="#FFFFFF">
                {refreshLoadWarning ? (
                  <Box sx={{ maxWidth: "1400px", mx: "auto", width: "100%" }}>
                    <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                      <Alert
                        severity="warning"
                        action={
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                void fetchBudgetData(true);
                              }}
                            >
                              Retry
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                window.location.reload();
                              }}
                            >
                              Refresh
                            </Button>
                          </Box>
                        }
                      >
                        {refreshLoadWarning}
                      </Alert>
                    </Box>
                  </Box>
                ) : null}
                {/* Lightweight overlay when refreshing data but keeping current content visible */}
                {isRefreshing && (
                  <Box
                    sx={{
                      position: "fixed",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                      zIndex: 1200,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        borderRadius: "999px",
                        p: 2,
                        boxShadow: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    pb: 6,
                    maxWidth: "1400px",
                    mx: "auto",
                    overflowX: { xs: "visible", md: "clip" },
                    width: "100%",
                  }}
                >
                  {/* Header */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: "26px", md: "32px" },
                        fontWeight: 700,
                        color: "#0F766E",
                        mb: 1,
                      }}
                    >
                      Your Budget Breakdown
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "15px",
                        color: "#64748B",
                        lineHeight: 1.6,
                      }}
                    >
                      Here is a breakdown of the budget information that you
                      have provided us. If you would like to change any details,
                      click on the edit icon.
                    </Typography>
                  </Box>

                  {/* Budget Summary Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#1E293B",
                        mb: 2.5,
                      }}
                    >
                      Budget Summary
                    </Typography>
                    {/* Horizontal scrollable container for mobile */}
                    <Box
                      sx={{
                        display: { xs: "flex", md: "none" },
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        overflowY: "visible",
                        overscrollBehaviorX: "contain",
                        WebkitOverflowScrolling: "touch",
                        gap: 1.5,
                        mb: 3,
                        pb: 2,
                        pt: 1,
                        pr: 2,
                        pl: 0,
                        width: "100%",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                      }}
                    >
                      {summaryCards.map((card) => (
                        <Box
                          key={card.id}
                          sx={{
                            minWidth: "180px",
                            maxWidth: "180px",
                            flex: "0 0 auto",
                          }}
                        >
                          <SummaryCardComponent
                            card={card}
                            onClick={() => handleSummaryCardClick(card.id)}
                            isActive={activeCardId === card.id}
                          />
                        </Box>
                      ))}
                    </Box>
                    {/* Desktop layout - flexible width cards */}
                    <Box
                      sx={{
                        display: { xs: "none", md: "flex" },
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: "30px",
                        mb: 3,
                        flexWrap: "nowrap",
                        width: "100%",
                      }}
                    >
                      {summaryCards.map((card) => (
                        <Box
                          key={card.id}
                          sx={{
                            flex: "1 1 0",
                            minWidth: 0, // Important for flex items to shrink below content size
                          }}
                        >
                          <SummaryCardComponent
                            card={card}
                            onClick={() => handleSummaryCardClick(card.id)}
                            isActive={activeCardId === card.id}
                          />
                        </Box>
                      ))}
                    </Box>
                    {/* Total Leftover */}
                    <Box
                      sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid rgba(203, 213, 225, 0.6)",
                        borderRadius: "12px",
                        boxShadow: "0px 1px 2px 0px #0000000D",
                        px: 2.5,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#1E293B",
                        }}
                      >
                        Total Leftover
                      </Typography>

                      {loading ? (
                        <Skeleton variant="text" width={150} height={28} />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "18px",
                              fontWeight: 700,
                              color: "#003838",
                            }}
                          >
                            {formatGBP(calculatedTotals?.balanceAmount ?? 0)}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#003838",
                            }}
                          >
                            /month
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {/* Review Budget Link and Important Notice Container */}
                    {false && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: 0,
                          gap: "24px",
                          width: { xs: "100%", md: "1216px" },
                          flex: "none",
                          order: 1,
                          alignSelf: "stretch",
                          flexGrow: 0,
                        }}
                      >
                        {/* Review Budget Link */}
                        <Button
                          onClick={handleReviewBudgetClick}
                          endIcon={<ArrowRightIcon />}
                          sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            padding: 0,
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Review your budget summary 🛡️
                        </Button>

                        {/* Important Notice */}
                        <Alert
                          severity="warning"
                          sx={{
                            backgroundColor: "#FFF7D6",
                            border: "1px solid #FACC15",
                            borderRadius: "8px",
                            width: "100%",
                            "& .MuiAlert-icon": {
                              color: "#CA8A04",
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "14px", color: "#92400E" }}
                          >
                            <strong>Important Notice</strong>
                            <br />
                            Please put <strong>£0</strong> in unanswered
                            questions to ensure accurate calculations.
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                    {/* New Rewiew Budget Link */}
                    {triggersCount > 0 ? (
                      <Button
                        onClick={() => {
                          setSmartReviewOpen(true);
                        }}
                        endIcon={
                          <ArrowForwardIcon
                            sx={{ color: ThemeColours.labelVar2, fontSize: 20 }}
                          />
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 500,
                          color: ThemeColours.labelVar2,
                          padding: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Check your smart reviews{" "}
                        <Box sx={{ marginLeft: 0.5 }}>
                          {normalisedCount > 0 && (
                            <CircleNumber
                              number={normalisedCount}
                              bgColor={ThemeColours.neon1 ?? "#0f0"}
                              size={16}
                              color={ThemeColours.labelVar2 ?? "#fff"}
                            />
                          )}
                        </Box>
                      </Button>
                    ) : null}
                  </Box>

                  <Divider sx={{ mb: 4 }} />

                  {/* Income Section */}
                  <Box
                    id="section-income"
                    ref={incomeSectionRef}
                    sx={{ mb: 3, scrollMarginTop: "120px" }}
                  >
                    <SectionHeader
                      icon={
                        <img
                          src="/assets/budgetBreakdown/Income.svg"
                          alt=""
                          style={{ width: 22, height: 22 }}
                        />
                      }
                      title="Income"
                      subtitle={incomeSubtitle}
                      expanded={expandedSections.income}
                      onToggle={() => toggleSection("income")}
                      onEdit={() => handleEdit("income")}
                      editStartIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      expandIcon={<ExpandMoreIcon />}
                    />
                    <Collapse in={!!expandedSections.income}>
                      <Grid container spacing={2}>
                        {incomeItems.map((item, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <BudgetItemComponent
                              item={item}
                              onEdit={() =>
                                handleEditItem("income", item.label)
                              }
                              editIcon={<EditIcon sx={{ fontSize: 18 }} />}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Assets Section */}
                  <Box
                    id="section-assets"
                    ref={assetsSectionRef}
                    sx={{ mb: 3, scrollMarginTop: "120px" }}
                  >
                    <SectionHeader
                      icon={
                        <img
                          src="/assets/budgetBreakdown/Assets.svg"
                          alt=""
                          style={{ width: 22, height: 22 }}
                        />
                      }
                      title="Assets"
                      subtitle={assetsSubtitle}
                      expanded={expandedSections.assets}
                      onToggle={() => toggleSection("assets")}
                      onEdit={() => handleEdit("assets")}
                      editStartIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      expandIcon={<ExpandMoreIcon />}
                    />
                    <Collapse in={!!expandedSections.assets}>
                      <Grid container spacing={2}>
                        {assetItems.map((item, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <BudgetItemComponent
                              item={item}
                              onEdit={() =>
                                handleEditItem("assets", item.label)
                              }
                              editIcon={<EditIcon sx={{ fontSize: 18 }} />}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Fixed expenses Section */}
                  <Box
                    id="section-fixedExpense"
                    ref={fixedExpenseSectionRef}
                    sx={{ mb: 3, scrollMarginTop: "120px" }}
                  >
                    <SectionHeader
                      icon={
                        <img
                          src="/assets/budgetBreakdown/Fixed.svg"
                          alt=""
                          style={{ width: 22, height: 22 }}
                        />
                      }
                      title="Fixed expenses"
                      subtitle={fixedSubtitle}
                      expanded={expandedSections.fixedExpense}
                      onToggle={() => toggleSection("fixedExpense")}
                      onEdit={() => handleEdit("fixedExpense")}
                      editStartIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      expandIcon={<ExpandMoreIcon />}
                    />
                    <Collapse in={!!expandedSections.fixedExpense}>
                      <Grid container spacing={2}>
                        {fixedExpenseList.map((row, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={`${row.innerKey}-${index}`}
                          >
                            <BudgetItemComponent
                              item={row.item}
                              onEdit={() => {
                                const currentScrollY =
                                  window.pageYOffset ||
                                  document.documentElement.scrollTop ||
                                  document.body.scrollTop ||
                                  window.scrollY ||
                                  0;
                                setSavedScrollPosition(currentScrollY);
                                setEditDialogConfig({
                                  groupKey: row.groupKey as any,
                                  fieldKey: row.fieldKey,
                                  title: `Edit ${row.item.label}`,
                                });
                                safeOpenEditDialog();
                              }}
                              editIcon={<EditIcon sx={{ fontSize: 18 }} />}
                            />
                            {String(row.groupKey) === "savings" &&
                            savingsLimitExceeded ? (
                              <Box sx={{ mt: 1 }}>
                                <Alert severity="warning">
                                  <Typography variant="body2">
                                    <strong>Savings Limit</strong>
                                    <br />
                                    You've entered more than £25 per month. The
                                    maximum allowed for savings is £25 per
                                    budget. Please adjust the amount or provide
                                    a reason in Notes.
                                  </Typography>
                                </Alert>
                              </Box>
                            ) : null}
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Flexible expenses Section */}
                  <Box
                    id="section-flexibleExpense"
                    ref={flexibleExpenseSectionRef}
                    sx={{ mb: 3, scrollMarginTop: "120px" }}
                  >
                    <SectionHeader
                      icon={
                        <img
                          src="/assets/budgetBreakdown/Flexible.svg"
                          alt=""
                          style={{ width: 22, height: 22 }}
                        />
                      }
                      title="Flexible expenses"
                      subtitle={flexibleSubtitle}
                      expanded={expandedSections.flexibleExpense}
                      onToggle={() => toggleSection("flexibleExpense")}
                      onEdit={() => handleEdit("flexibleExpense")}
                      editStartIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      expandIcon={<ExpandMoreIcon />}
                    />
                    <Collapse in={!!expandedSections.flexibleExpense}>
                      <Grid container spacing={2}>
                        {flexibleExpenseList.map((row, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={`${row.innerKey}-${index}`}
                          >
                            <BudgetItemComponent
                              item={row.item}
                              onEdit={() => {
                                const currentScrollY =
                                  window.pageYOffset ||
                                  document.documentElement.scrollTop ||
                                  document.body.scrollTop ||
                                  window.scrollY ||
                                  0;
                                setSavedScrollPosition(currentScrollY);
                                setEditDialogConfig({
                                  groupKey: row.groupKey as any,
                                  fieldKey: row.fieldKey,
                                  title: `Edit ${row.item.label}`,
                                });
                                safeOpenEditDialog();
                              }}
                              editIcon={<EditIcon sx={{ fontSize: 18 }} />}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Debts Section */}
                  <Box
                    id="section-debts"
                    ref={debtsSectionRef}
                    sx={{ mb: 3, scrollMarginTop: "120px" }}
                  >
                    <SectionHeader
                      icon={
                        <img
                          src="/assets/budgetBreakdown/Debts.svg"
                          alt=""
                          style={{ width: 22, height: 22 }}
                        />
                      }
                      title="Debts"
                      subtitle={debtsSubtitle}
                      expanded={expandedSections.debts}
                      onToggle={() => toggleSection("debts")}
                      onEdit={() =>
                        navigate("/update-budget?outer=debts&inner=debts")
                      }
                      editStartIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      expandIcon={<ExpandMoreIcon />}
                    />
                    <Collapse in={!!expandedSections.debts}>
                      <Box>
                        {debtsLoading && (
                          <Box sx={{ mb: 4 }}>
                            <Skeleton
                              variant="text"
                              width="30%"
                              height={28}
                              sx={{ mb: 2 }}
                            />
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                              {[1, 2].map((i) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={`debt-skel-top-${i}`}
                                >
                                  <Skeleton
                                    variant="rectangular"
                                    height={120}
                                    sx={{ borderRadius: "12px" }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            <Skeleton
                              variant="text"
                              width="35%"
                              height={28}
                              sx={{ mb: 2 }}
                            />
                            <Grid container spacing={2}>
                              {[1, 2].map((i) => (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={`debt-skel-bot-${i}`}
                                >
                                  <Skeleton
                                    variant="rectangular"
                                    height={120}
                                    sx={{ borderRadius: "12px" }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                        {!debtsLoading && (
                          <>
                            {/* Debt Administration Fee */}
                            {debtAdminFee && (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    mb: 2,
                                  }}
                                >
                                  Debt Administration
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <BudgetItemComponent
                                      item={{
                                        label: "Debt Administration Fee",
                                        amount: debtAdminFee.amount,
                                        frequency: debtAdminFee.frequency,
                                        editable: true,
                                      }}
                                      onEdit={() => {
                                        const currentScrollY =
                                          window.pageYOffset ||
                                          document.documentElement.scrollTop ||
                                          document.body.scrollTop ||
                                          window.scrollY ||
                                          0;
                                        setSavedScrollPosition(currentScrollY);
                                        setEditDialogConfig({
                                          groupKey:
                                            "debtsAdministration" as any,
                                          fieldKey: "administrationFee",
                                          title: "Edit debt administration fee",
                                        });
                                        safeOpenEditDialog();
                                      }}
                                      editIcon={
                                        <EditIcon sx={{ fontSize: 18 }} />
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </>
                            )}

                            {/* Priority Debts */}
                            {priorityDebts.length > 0 && (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    mb: 2,
                                  }}
                                >
                                  Priority debts
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                  {priorityDebts.map((debt, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={6}
                                      key={debt.id}
                                    >
                                      <DebtCardComponent
                                        debt={debt}
                                        index={index}
                                        onEdit={() =>
                                          navigate(
                                            "/update-budget?outer=debts&inner=debts"
                                          )
                                        }
                                        editIcon={
                                          <EditIcon sx={{ fontSize: 18 }} />
                                        }
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </>
                            )}

                            {/* Non-Priority Debts */}
                            {nonPriorityDebts.length > 0 && (
                              <>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    mb: 2,
                                  }}
                                >
                                  Non-Priority debts
                                </Typography>
                                <Grid container spacing={2}>
                                  {nonPriorityDebts.map((debt, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={6}
                                      key={debt.id}
                                    >
                                      <DebtCardComponent
                                        debt={debt}
                                        index={index}
                                        onEdit={() =>
                                          navigate(
                                            "/update-budget?outer=debts&inner=debts"
                                          )
                                        }
                                        editIcon={
                                          <EditIcon sx={{ fontSize: 18 }} />
                                        }
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </>
                            )}

                            {/* No Debts Message */}
                            {!debtAdminFee &&
                              priorityDebts.length === 0 &&
                              nonPriorityDebts.length === 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    color: "#64748B",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    py: 4,
                                  }}
                                >
                                  No debts have been added yet.
                                </Typography>
                              )}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Additional Notes (Customer Profile → textField1) */}
                  <AdditionalNotesSection
                    profileData={profileData}
                    setProfileData={setProfileData}
                    userId={user?.userId}
                  />

                  <Divider sx={{ my: 4 }} />

                  {/* Footer Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate("/update-budget?outer=income&inner=salary")
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: "15px",
                        fontWeight: 600,
                        px: 4,
                        py: 1.25,
                        borderRadius: "8px",
                        borderColor: "#CBD5E1",
                        color: "#475569",
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          borderColor: "#94A3B8",
                          backgroundColor: "#F8FAFC",
                        },
                      }}
                    >
                      Edit Budget
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowRightIcon />}
                      onClick={() => navigate("/documents")}
                      sx={{
                        backgroundColor: "#004D44",
                        textTransform: "none",
                        fontSize: "15px",
                        fontWeight: 600,
                        px: 4,
                        py: 1.25,
                        borderRadius: "8px",
                        boxShadow: "none",
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          backgroundColor: "#004D44",
                          boxShadow: "none",
                          border: "0.5px solid #004D44",
                        },
                      }}
                    >
                      Upload documents
                    </Button>
                  </Box>
                </Box>
              </DashboardLayout>
            )}
            {/* Loading overlay for dialog (rendered once, outside DashboardLayout to avoid duplication) */}
            {isDialogLoading && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    minWidth: "200px",
                  }}
                >
                  <Skeleton variant="circular" width={40} height={40} />
                  <Typography variant="body2" sx={{ color: "#64748B" }}>
                    Loading...
                  </Typography>
                </Box>
              </Box>
            )}
            {renderSmartReviewDialog()}
            {/* Single-field edit dialog (Salary → takeHomeSalary/basicIncome) rendered once */}
            <EditFieldDialog
              open={isEditFieldDialogOpen && !!editDialogConfig}
              onClose={() => {
                setIsEditFieldDialogOpen(false);
                setEditDialogConfig(null);
                setIsDialogLoading(false);
              }}
              onWarningAction={handleWarningDialog}
              onSaved={() => {
                // Force refresh budget data after successful save (bypass cache)
                fetchBudgetData(true).then(() => {
                  // Restore the saved scroll position after data is loaded
                  if (savedScrollPosition > 0) {
                    requestAnimationFrame(() => {
                      setTimeout(() => {
                        try {
                          // Force scroll on mobile by using multiple methods
                          window.scrollTo(0, savedScrollPosition);
                          document.documentElement.scrollTop =
                            savedScrollPosition;
                          document.body.scrollTop = savedScrollPosition;

                          // Double-check and retry if needed
                          setTimeout(() => {
                            const currentScroll =
                              window.pageYOffset ||
                              document.documentElement.scrollTop ||
                              document.body.scrollTop ||
                              0;

                            if (
                              Math.abs(currentScroll - savedScrollPosition) > 10
                            ) {
                              window.scrollTo(0, savedScrollPosition);
                            }
                          }, 100);
                        } catch (e) {
                          console.error("Error restoring scroll position:", e);
                        }
                      }, 200); // Longer delay for mobile
                    });
                  }
                });
              }}
              groupKey={editDialogConfig?.groupKey ?? "salary"}
              fieldKey={editDialogConfig?.fieldKey ?? "takeHomeSalary"}
              title={editDialogConfig?.title ?? "Edit income"}
              isNorthernIreland={isNorthernIreland}
              niExpenseData={
                expenseRecord
                  ? {
                      waterBill: expenseRecord.waterBill,
                      waterWaste: expenseRecord.waterWaste,
                    }
                  : undefined
              }
            />
            {/* Review Budget Summary Dialog rendered once */}
            <ReviewBudgetSummaryDialog
              open={isReviewBudgetDialogOpen}
              onClose={() => {
                setIsReviewBudgetDialogOpen(false);
                // Remove showSummary parameter from URL when dialog closes
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete("showSummary");
                window.history.replaceState({}, "", newUrl.toString());
              }}
            />

            {/* Profile warning dialog (employment status / accommodation etc.) triggered by red-warning tokens */}
            {profileDialogState ? (
              <ProfileFormDialog
                open={!!profileDialogState}
                title={profileDialogState.title}
                schema={profileDialogState.schema as any}
                initialData={profileDialogState.initialData}
                formId={`budget-profile-warning-${profileDialogState.key}`}
                onSubmit={handleProfileDialogSubmit}
                onClose={() => setProfileDialogState(null)}
                isSaving={isProfileDialogSaving}
                overlay={
                  confirmOpen ? (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        p: 2,
                      }}
                    >
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: "100%",
                          maxWidth: 560,
                          backgroundColor: "white",
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 24,
                        }}
                      >
                        <DialogTitle sx={{ fontWeight: 700 }}>
                          Confirm
                        </DialogTitle>
                        <DialogContent dividers>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {CONFIRM_REMOVE_SECTION_MESSAGE}
                          </Typography>
                          {confirmClearedLabels.length > 0 ? (
                            <Box sx={{ pl: 2 }}>
                              {confirmClearedLabels.map((label) => (
                                <Typography
                                  key={label}
                                  variant="body2"
                                  sx={{ mb: 0.5 }}
                                >
                                  • {label}
                                </Typography>
                              ))}
                            </Box>
                          ) : null}
                        </DialogContent>
                        <DialogActions>
                          <Button
                            variant="outlined"
                            onClick={handleConfirmCancel}
                            disabled={!confirmArmed}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              void handleConfirmContinue();
                            }}
                            disabled={!confirmArmed}
                          >
                            Continue
                          </Button>
                        </DialogActions>
                      </Box>
                    </Box>
                  ) : null
                }
                onWarningAction={(
                  action?: OpenActionSpec,
                  metadata?: { varId?: string }
                ) => {
                  if (!action) return false;
                  return handleWarningDialog(action, metadata);
                }}
              />
            ) : null}
          </>
        </TriggersProvider>
      </WarningsRefreshContext.Provider>
    </CustomerMetadataContext.Provider>
  );
};

const BudgetPage: React.FC = () => {
  return <BudgetPageInner />;
};

export default BudgetPage;
